import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import PromotionPreparation from '../components/PromotionPreparation';
import { authenticatedFetch } from '../utils/api';
import ReportEditor from '../components/ReportEditor';
import InstallPWA from '../components/InstallPWA';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import { 
  Building2, 
  MapPin, 
  User,
  LogOut,
  Plus,
  Copy,
  Trash2,
  RefreshCw,
  School,
  CheckCircle,
  XCircle,
  Loader2,
  Users,
  FileText,
  Download,
  Edit,
  Save,
  X,
  FileDown,
  Menu,
  Eye,
  Clock,
  ChevronDown,
  ChevronRight,
  BookOpen,
  Briefcase,
  UserCheck,
  Calendar,
  Upload,
  Stamp,
  FileEdit,
  Sparkles,
  Link,
  Unlink,
  Network,
  CreditCard,
  Banknote,
  Forward,
  Paperclip,
  FileIcon,
  HeartHandshake,
  AlertTriangle,
  Target,
  ListChecks,
  GraduationCap,
  Send
} from 'lucide-react';

const SISODashboard = () => {
  const { user, logout } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [schoolName, setSchoolName] = useState('');
  const [creating, setCreating] = useState(false);
  
  // Edit state
  const [editingFolderId, setEditingFolderId] = useState(null);
  const [editSchoolName, setEditSchoolName] = useState('');
  const [editHeadteacherName, setEditHeadteacherName] = useState('');
  const [saving, setSaving] = useState(false);
  
  // Report generation state
  const [generatingReport, setGeneratingReport] = useState(null);
  const [generatingCircuitReport, setGeneratingCircuitReport] = useState(false);
  const [generatingAIReport, setGeneratingAIReport] = useState(false);
  const [selectedReportType, setSelectedReportType] = useState('start_of_term'); // 'start_of_term' or 'end_of_term'
  
  // Director Connection state
  const [ddConnectionStatus, setDdConnectionStatus] = useState(null);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [connectTokenInput, setConnectTokenInput] = useState('');
  const [connectingToDD, setConnectingToDD] = useState(false);
  
  // Work Output state
  const [schoolWorkOutput, setSchoolWorkOutput] = useState({});
  const [loadingWorkOutput, setLoadingWorkOutput] = useState(null);
  const workOutputWeeks = Array.from({ length: 15 }, (_, i) => i + 1);
  
  // Report Data state (Start/End of Term data from headteachers)
  const [schoolReportData, setSchoolReportData] = useState({});
  const [loadingReportData, setLoadingReportData] = useState(null);
  
  // Report Editor state
  const [isReportEditorOpen, setIsReportEditorOpen] = useState(false);
  const [currentReportData, setCurrentReportData] = useState(null);
  const [currentReportType, setCurrentReportType] = useState('start_of_term');

  // District Capital Town state
  const [districtCapitalTown, setDistrictCapitalTown] = useState('');
  const [savingTown, setSavingTown] = useState(false);
  const [townLoaded, setTownLoaded] = useState(false);

  // Offline/Cache state
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [dataFromCache, setDataFromCache] = useState(false);

  // Supervision state
  const [supervisionData, setSupervisionData] = useState(null);
  const [loadingSupervision, setLoadingSupervision] = useState(false);
  const [expandedSchools, setExpandedSchools] = useState({});
  const [selectedTeacher, setSelectedTeacher] = useState(null);
  const [teacherDetails, setTeacherDetails] = useState(null);
  const [loadingTeacherDetails, setLoadingTeacherDetails] = useState(false);
  const [expandedSubmissionWeeks, setExpandedSubmissionWeeks] = useState({});

  // Support Action state
  const [showSupportActionModal, setShowSupportActionModal] = useState(false);
  const [supportActionTeacher, setSupportActionTeacher] = useState(null);
  const [supportActionPerformance, setSupportActionPerformance] = useState(null);
  const [loadingSupportPerformance, setLoadingSupportPerformance] = useState(false);
  const [supportActionForm, setSupportActionForm] = useState({
    action_type: 'lesson_notes_support',
    action_title: '',
    action_description: '',
    recommended_actions: [''],
    follow_up_date: ''
  });
  const [submittingSupportAction, setSubmittingSupportAction] = useState(false);
  // Profile/Settings state
  const [userProfile, setUserProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [uploadingProfilePic, setUploadingProfilePic] = useState(false);
  const [uploadingSignature, setUploadingSignature] = useState(false);
  const [uploadingStamp, setUploadingStamp] = useState(false);
  const [profileFormData, setProfileFormData] = useState({
    town: '',
    phone: '',
    file_number: ''
  });
  const [savingProfile, setSavingProfile] = useState(false);

  // Applications state
  const [applications, setApplications] = useState([]);
  const [loadingApplications, setLoadingApplications] = useState(false);
  const [newAppsCount, setNewAppsCount] = useState(0);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [showApplicationDetail, setShowApplicationDetail] = useState(false);
  const [showHeadteacherMemoTab, setShowHeadteacherMemoTab] = useState(false);
  const [processingAction, setProcessingAction] = useState(false);
  const [disapproveComment, setDisapproveComment] = useState('');
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [escalatingApp, setEscalatingApp] = useState(null);
  const [escalateMemoText, setEscalateMemoText] = useState('');
  const [formattedDirectorMemo, setFormattedDirectorMemo] = useState('');
  const [generatingMemo, setGeneratingMemo] = useState(false);
  const [showMemoPreview, setShowMemoPreview] = useState(false);
  const [isEditingMemo, setIsEditingMemo] = useState(false);
  const [editedMemoHtml, setEditedMemoHtml] = useState('');
  const [memoSaved, setMemoSaved] = useState(false);
  const [savingMemo, setSavingMemo] = useState(false);
  const [generatingEscalationPDF, setGeneratingEscalationPDF] = useState(false);
  const [escalationPDFReady, setEscalationPDFReady] = useState(false);
  const [applicationSupportFee, setApplicationSupportFee] = useState(0);
  const [processingPayment, setProcessingPayment] = useState(false);

  // Utility function to format memo for display (strips technical markers)
  const formatLetterForDisplay = (letter) => {
    if (!letter) return '';
    
    let formatted = letter
      .replace(/\[RIGHT\]\s*/g, '')
      .replace(/\[LEFT\]\s*/g, '')
      .replace(/\[CENTER\]\s*/g, '')
      .replace(/\[BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
      .replace(/\[SIGNATURE_PLACEHOLDER\]/g, '\n\n____________________\n(Signature)')
      .replace(/\[STAMP_PLACEHOLDER\]/g, '\n\n[Official Stamp]')
      .replace(/\[SIGNATURE\]/g, '\n\n____________________\n(Signature)')
      .replace(/\[STAMP\]/g, '\n\n[Official Stamp]')
      .replace(/```/g, '');
    
    return formatted.trim();
  };

  // Convert structured text (with tags like [CENTER], [BOLD]) to HTML for Tiptap
  const convertStructuredTextToHtml = (text) => {
    if (!text) return '';
    let fullText = text
      .replace(/\[BOLD_UNDERLINE\]([\s\S]*?)\[\/BOLD_UNDERLINE\]/g, '<strong><u>$1</u></strong>')
      .replace(/\[CENTER\]([\s\S]*?)\[\/CENTER\]/g, '<center_block>$1</center_block>')
      .replace(/\[RIGHT_ALIGN\]([\s\S]*?)\[\/RIGHT_ALIGN\]/g, '<right_block>$1</right_block>')
      .replace(/\[RIGHT\]([\s\S]*?)\[\/RIGHT\]/g, '<right_block>$1</right_block>')
      .replace(/\[SIGNATURE_PLACEHOLDER\]/gi, '<right_block><em>(Signature)</em></right_block>')
      .replace(/\[SIGNATURE\]/gi, '<right_block><em>(Signature)</em></right_block>')
      .replace(/\[STAMP_PLACEHOLDER\]/gi, '<right_block><em>(Stamp)</em></right_block>')
      .replace(/\[STAMP\]/gi, '<right_block><em>(Stamp)</em></right_block>')
      .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
      .replace(/\[\/?RIGHT\]/g, '')
      .replace(/\[\/?RIGHT_ALIGN\]/g, '')
      .replace(/\[\/?CENTER\]/g, '')
      .replace(/\[\/?BOLD\]/g, '')
      .replace(/\[\/?UNDERLINE\]/g, '');
    let html = fullText.split('\n\n').map(para => {
      let processed = para
        .replace(/\[BOLD\](.*?)\[\/BOLD\]/g, '<strong>$1</strong>')
        .replace(/\[UNDERLINE\](.*?)\[\/UNDERLINE\]/g, '<u>$1</u>')
        .replace(/<center_block>([\s\S]*?)<\/center_block>/g, '<p style="text-align: center;">$1</p>')
        .replace(/<right_block>([\s\S]*?)<\/right_block>/g, '<p style="text-align: right;">$1</p>');
      if (!processed.startsWith('<p')) processed = `<p>${processed}</p>`;
      return processed;
    }).join('');
    html = html.replace(/\n/g, '<br/>');
    return html;
  };

  // Render formatted memo with proper styling for preview
  const renderFormattedLetter = (letter, signatureUrl = null, stampUrl = null) => {
    if (!letter) return null;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
    const lines = letter.split('\n');
    const elements = [];
    let currentAlignment = 'left';
    
    lines.forEach((line, index) => {
      let processedLine = line;
      let lineAlignment = currentAlignment;
      let isHeading = false;
      
      // Check for alignment markers and update current alignment state
      if (processedLine.includes('[RIGHT]')) {
        lineAlignment = 'right';
        currentAlignment = 'right';
        processedLine = processedLine.replace(/\[RIGHT\]\s*/g, '');
      } else if (processedLine.includes('[LEFT]')) {
        lineAlignment = 'left';
        currentAlignment = 'left';
        processedLine = processedLine.replace(/\[LEFT\]\s*/g, '');
      } else if (processedLine.includes('[CENTER]')) {
        lineAlignment = 'center';
        currentAlignment = 'center';
        processedLine = processedLine.replace(/\[CENTER\]\s*/g, '');
      }
      
      if (processedLine.includes('[BOLD_UNDERLINE]') || processedLine.includes('[/BOLD_UNDERLINE]')) {
        isHeading = processedLine.includes('[BOLD_UNDERLINE]');
        processedLine = processedLine.replace(/\[\/?BOLD_UNDERLINE\]/g, '');
      }
      
      // Also detect heading by pattern: centered line that looks like a subject heading
      if (!isHeading && lineAlignment === 'center') {
        const upperLine = processedLine.toUpperCase().trim();
        if (upperLine.startsWith('APPLICATION') || 
            upperLine.startsWith('RE:') || 
            upperLine.startsWith('SUBJECT:') ||
            upperLine.startsWith('MEMORANDUM') ||
            (upperLine === processedLine.trim() && upperLine.length > 10 && upperLine.length < 100)) {
          isHeading = true;
        }
      }
      
      if (processedLine.includes('[SIGNATURE_PLACEHOLDER]') || processedLine.includes('[SIGNATURE]')) {
        // Set alignment to right for all content after signature
        currentAlignment = 'right';
        
        if (signatureUrl) {
          elements.push(
            <div key={index} className="flex justify-end mt-4 mb-2">
              <img 
                src={`${backendUrl}${signatureUrl}`} 
                alt="Digital Signature" 
                className="h-16 w-auto object-contain"
              />
            </div>
          );
        } else {
          elements.push(
            <div key={index} className="flex justify-end mt-4 mb-2">
              <div>
                <div className="border-b-2 border-gray-400 w-48 mb-1"></div>
                <div className="text-sm text-gray-500 italic">(Signature - upload in Profile)</div>
              </div>
            </div>
          );
        }
        return;
      }
      
      if (processedLine.includes('[STAMP_PLACEHOLDER]') || processedLine.includes('[STAMP]')) {
        if (stampUrl) {
          elements.push(
            <div key={index} className="flex justify-end my-2">
              <img 
                src={`${backendUrl}${stampUrl}`} 
                alt="Official Stamp" 
                className="h-20 w-auto object-contain"
              />
            </div>
          );
        } else {
          elements.push(
            <div key={index} className="flex justify-end my-2">
              <div className="border-2 border-dashed border-gray-300 px-4 py-2 text-sm text-gray-400">
                [Official Stamp - upload in Profile]
              </div>
            </div>
          );
        }
        return;
      }
      
      // Remove code fence markers, plaintext tags, and ALL closing alignment/formatting tags
      processedLine = processedLine
        .replace(/```/g, '')
        .replace(/`plaintext`?/gi, '')
        .replace(/plaintext/gi, '')
        .replace(/\[\/?BOLD_UNDERLINE\]/g, '')
        .replace(/\[\/?CENTER\]/g, '')
        .replace(/\[\/?RIGHT\]/g, '')
        .replace(/\[\/?LEFT\]/g, '')
        .replace(/\[\/?RIGHT_ALIGN\]/g, '')
        .replace(/\[\/?BOLD\]/g, '')
        .replace(/\[\/?UNDERLINE\]/g, '')
        .trim();
      
      if (!processedLine) {
        elements.push(<div key={index} className="h-3"></div>);
        return;
      }
      
      // Determine alignment classes - use flexbox for proper positioning
      let containerClass = 'flex';
      if (lineAlignment === 'right') {
        containerClass = 'flex justify-end';
      } else if (lineAlignment === 'center') {
        containerClass = 'flex justify-center';
      } else {
        containerClass = 'flex justify-start';
      }
      
      if (isHeading) {
        elements.push(
          <div key={index} className="flex justify-center my-4">
            <span 
              style={{ 
                fontWeight: 'bold', 
                textDecoration: 'underline', 
                textUnderlineOffset: '4px',
                textTransform: 'uppercase',
                fontSize: '1rem'
              }}
            >
              {processedLine}
            </span>
          </div>
        );
      } else {
        elements.push(
          <div key={index} className={containerClass}>
            <span>{processedLine}</span>
          </div>
        );
      }
    });
    
    return elements;
  };

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      localStorage.removeItem('auth_token');
      window.location.href = '/';
    }
  };

  // Load dashboard data
  const loadDashboard = async () => {
    setLoading(true);
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/dashboard`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDashboardData(data);
        setDataFromCache(false);
        setIsOffline(false);
      } else {
        throw new Error('Failed to load dashboard');
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      setIsOffline(!navigator.onLine);
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  // Load SISO settings (district capital town)
  const loadSISOSettings = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/settings`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDistrictCapitalTown(data.district_capital_town || '');
        setTownLoaded(true);
      }
    } catch (error) {
      console.error('Error loading SISO settings:', error);
    }
  };

  // Save district capital town
  const saveDistrictCapitalTown = async () => {
    setSavingTown(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ district_capital_town: districtCapitalTown })
      });

      if (response.ok) {
        toast.success("District capital town saved! It will appear in your generated reports.");
      } else {
        throw new Error('Failed to save');
      }
    } catch (error) {
      toast.error("Failed to save district capital town");
    } finally {
      setSavingTown(false);
    }
  };

  // Load Director connection status
  const loadDDConnectionStatus = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/dd-supervision-connection`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setDdConnectionStatus(data);
      }
    } catch (error) {
      console.error('Error loading DD connection status:', error);
    }
  };

  // Connect to Director using token
  const connectToDDSupervision = async () => {
    if (!connectTokenInput.trim()) {
      toast.error('Please enter a connect token');
      return;
    }
    
    setConnectingToDD(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/connect-to-dd-supervision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ connect_token: connectTokenInput.trim() })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowConnectModal(false);
        setConnectTokenInput('');
        loadDDConnectionStatus();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to connect');
      }
    } catch (error) {
      toast.error('Error connecting to Director');
    } finally {
      setConnectingToDD(false);
    }
  };

  // Disconnect from Director
  const disconnectFromDD = async () => {
    if (!window.confirm('Are you sure you want to disconnect from Director?')) {
      return;
    }
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/disconnect-from-dd-supervision`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Disconnected from DD-Supervision');
        setDdConnectionStatus({ connected: false });
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to disconnect');
      }
    } catch (error) {
      toast.error('Error disconnecting');
    }
  };

  // Load supervision data (schools with teachers)
  const loadSupervisionData = async () => {
    setLoadingSupervision(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/supervision/schools`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSupervisionData(data);
      } else {
        throw new Error('Failed to load supervision data');
      }
    } catch (error) {
      console.error('Error loading supervision data:', error);
      toast.error("Failed to load supervision data");
    } finally {
      setLoadingSupervision(false);
    }
  };

  // Load teacher details
  const loadTeacherDetails = async (teacherId) => {
    setLoadingTeacherDetails(true);
    setSelectedTeacher(teacherId);
    setExpandedSubmissionWeeks({}); // Reset expanded weeks when selecting new teacher
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/supervision/teacher/${teacherId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setTeacherDetails(data);
      } else {
        throw new Error('Failed to load teacher details');
      }
    } catch (error) {
      console.error('Error loading teacher details:', error);
      toast.error("Failed to load teacher details");
    } finally {
      setLoadingTeacherDetails(false);
    }
  };

  // Toggle school expansion
  const toggleSchoolExpansion = (schoolId) => {
    setExpandedSchools(prev => ({
      ...prev,
      [schoolId]: !prev[schoolId]
    }));
    // Clear selected teacher when collapsing
    if (expandedSchools[schoolId]) {
      setSelectedTeacher(null);
      setTeacherDetails(null);
    }
  };


  // Open support action modal for a teacher
  const openSupportActionModal = async (teacher, headteacher, school) => {
    setSupportActionTeacher({ ...teacher, headteacher, school });
    setShowSupportActionModal(true);
    setLoadingSupportPerformance(true);
    setSupportActionForm({
      action_type: 'lesson_notes_support',
      action_title: '',
      action_description: '',
      recommended_actions: [''],
      follow_up_date: ''
    });
    
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/teacher-performance/${teacher.id}`);
      if (response.ok) {
        const data = await response.json();
        setSupportActionPerformance(data);
      }
    } catch (error) {
      console.error('Error loading teacher performance:', error);
    } finally {
      setLoadingSupportPerformance(false);
    }
  };

  // Submit support action
  const submitSupportAction = async () => {
    if (!supportActionForm.action_title.trim()) {
      toast.error('Please enter an action title');
      return;
    }
    if (!supportActionForm.action_description.trim()) {
      toast.error('Please describe the support action');
      return;
    }
    
    setSubmittingSupportAction(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/support-action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          teacher_id: supportActionTeacher.id,
          headteacher_id: supportActionTeacher.headteacher?.id,
          school_id: supportActionTeacher.school?.id,
          action_type: supportActionForm.action_type,
          action_title: supportActionForm.action_title,
          action_description: supportActionForm.action_description,
          recommended_actions: supportActionForm.recommended_actions.filter(a => a.trim()),
          performance_context: supportActionPerformance,
          follow_up_date: supportActionForm.follow_up_date || null
        })
      });
      
      if (response.ok) {
        toast.success('Support action created successfully! Teacher and Headteacher have been notified.');
        setShowSupportActionModal(false);
        setSupportActionTeacher(null);
        setSupportActionPerformance(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create support action');
      }
    } catch (error) {
      console.error('Error submitting support action:', error);
      toast.error('Failed to create support action');
    } finally {
      setSubmittingSupportAction(false);
    }
  };

  // Add/remove recommended action fields
  const addRecommendedAction = () => {
    setSupportActionForm(prev => ({
      ...prev,
      recommended_actions: [...prev.recommended_actions, '']
    }));
  };

  const removeRecommendedAction = (index) => {
    setSupportActionForm(prev => ({
      ...prev,
      recommended_actions: prev.recommended_actions.filter((_, i) => i !== index)
    }));
  };

  const updateRecommendedAction = (index, value) => {
    setSupportActionForm(prev => ({
      ...prev,
      recommended_actions: prev.recommended_actions.map((a, i) => i === index ? value : a)
    }));
  };


  // Create school folder
  const createSchoolFolder = async () => {
    if (!schoolName.trim()) {
      toast.error("Please enter a school name");
      return;
    }

    setCreating(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/school-folders`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ school_name: schoolName.trim() })
      });

      if (response.ok) {
        toast.success("School folder created!");
        setSchoolName('');
        setIsCreateDialogOpen(false);
        loadDashboard();
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to create folder');
      }
    } catch (error) {
      toast.error(error.message || "Failed to create school folder");
    } finally {
      setCreating(false);
    }
  };

  // Copy token
  const copyToken = (token) => {
    navigator.clipboard.writeText(token);
    toast.success("Token copied!");
  };

  // Delete folder
  const deleteFolder = async (folderId, schoolName) => {
    if (!window.confirm(`Delete "${schoolName}"?`)) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/school-folders/${folderId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Deleted");
        loadDashboard();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error("Failed to delete");
    }
  };

  // Start editing
  const startEditing = (folder) => {
    setEditingFolderId(folder.id);
    setEditSchoolName(folder.school_name);
    setEditHeadteacherName(folder.headteacher_name || '');
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingFolderId(null);
    setEditSchoolName('');
    setEditHeadteacherName('');
  };

  // Save edit
  const saveEdit = async (folderId) => {
    setSaving(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/school-folders/${folderId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          school_name: editSchoolName.trim() || undefined,
          headteacher_name: editHeadteacherName.trim() || undefined
        })
      });

      if (response.ok) {
        toast.success("Updated!");
        cancelEditing();
        loadDashboard();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error("Failed to update");
    } finally {
      setSaving(false);
    }
  };

  // Regenerate token
  const regenerateToken = async (folderId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/school-folders/${folderId}/regenerate-token`, {
        method: 'PUT',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Token regenerated!");
        loadDashboard();
      } else {
        throw new Error('Failed');
      }
    } catch (error) {
      toast.error("Failed");
    }
  };

  // Generate individual school report
  const generateSchoolReport = async (folderId, schoolName, reportType = selectedReportType) => {
    setGeneratingReport(folderId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/generate-report/${folderId}?report_type=${reportType}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const typeLabel = reportType === 'start_of_term' ? 'Start_of_Term' : 'End_of_Term';
        link.download = `${schoolName.replace(/\s+/g, '_')}_${typeLabel}_Report.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Downloaded!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error(error.message || "Failed to generate");
    } finally {
      setGeneratingReport(null);
    }
  };

  // Generate circuit report
  const generateCircuitReport = async (reportType = selectedReportType) => {
    setGeneratingCircuitReport(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/generate-circuit-report?report_type=${reportType}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        const typeLabel = reportType === 'start_of_term' ? 'Start_of_Term' : 'End_of_Term';
        link.download = `${dashboardData?.user?.circuit_name?.replace(/\s+/g, '_') || 'Circuit'}_${typeLabel}_Report.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        toast.success("Circuit report downloaded!");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate report');
      }
    } catch (error) {
      toast.error(error.message || "Failed to generate");
    } finally {
      setGeneratingCircuitReport(false);
    }
  };

  // Generate official circuit report (HTML for editing)
  const generateAIReport = async (reportType = selectedReportType) => {
    setGeneratingAIReport(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      toast.info("AI is generating your official report. This may take 30-60 seconds...", { duration: 15000 });
      
      const response = await authenticatedFetch(`${backendUrl}/api/siso/generate-ai-report-html`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ report_type: reportType })
      });

      if (response.ok) {
        const data = await response.json();
        setCurrentReportData(data);
        setCurrentReportType(reportType);
        setIsReportEditorOpen(true);
        toast.success("Report generated! Review and edit before finalizing.");
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to generate AI report');
      }
    } catch (error) {
      toast.error(error.message || "Failed to generate AI report");
    } finally {
      setGeneratingAIReport(false);
    }
  };

  // Load work output for a school
  const loadSchoolWorkOutput = async (folderId) => {
    setLoadingWorkOutput(folderId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/school-work-output/${folderId}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setSchoolWorkOutput(prev => ({
          ...prev,
          [folderId]: data.work_outputs || []
        }));
      } else {
        throw new Error('Failed to load work output');
      }
    } catch (error) {
      console.error('Error loading work output:', error);
      toast.error('Failed to load work output');
    } finally {
      setLoadingWorkOutput(null);
    }
  };

  // Load report data (attendance, teacher info) for a school
  const loadSchoolReportData = async (folderId, reportType) => {
    setLoadingReportData(folderId);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(
        `${backendUrl}/api/siso/school-report-data/${folderId}?report_type=${reportType}`,
        { credentials: 'include' }
      );

      if (response.ok) {
        const data = await response.json();
        setSchoolReportData(prev => ({
          ...prev,
          [folderId]: data
        }));
      } else {
        throw new Error('Failed to load report data');
      }
    } catch (error) {
      console.error('Error loading report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoadingReportData(null);
    }
  };

  // Load all schools' report data at once
  const loadAllSchoolsReportData = async (reportType) => {
    if (!dashboardData?.folders?.length) return;
    
    setLoadingReportData('all');
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Load data for all schools in parallel
      const promises = dashboardData.folders.map(async (folder) => {
        const response = await authenticatedFetch(
          `${backendUrl}/api/siso/school-report-data/${folder.id}?report_type=${reportType}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          return { folderId: folder.id, data };
        }
        return null;
      });

      const results = await Promise.all(promises);
      
      const newReportData = {};
      results.forEach(result => {
        if (result) {
          newReportData[result.folderId] = result.data;
        }
      });
      
      setSchoolReportData(prev => ({ ...prev, ...newReportData }));
      toast.success('Report data loaded successfully');
    } catch (error) {
      console.error('Error loading all report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoadingReportData(null);
    }
  };

  // Load user profile
  const loadUserProfile = async () => {
    setProfileLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/profile`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setUserProfile(data.profile);
        setProfileFormData({
          town: data.profile.town || '',
          phone: data.profile.phone || '',
          file_number: data.profile.file_number || ''
        });
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Failed to load profile');
    } finally {
      setProfileLoading(false);
    }
  };

  // Upload profile picture
  const handleProfilePictureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingProfilePic(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-profile-picture`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Profile picture uploaded!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload');
    } finally {
      setUploadingProfilePic(false);
    }
  };

  // Upload digital signature
  const handleSignatureUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingSignature(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-signature`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Signature uploaded!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload');
    } finally {
      setUploadingSignature(false);
    }
  };

  // Upload digital stamp
  const handleStampUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setUploadingStamp(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const formData = new FormData();
      formData.append('file', file);

      const response = await authenticatedFetch(`${backendUrl}/api/user/upload-stamp`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Stamp uploaded!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to upload');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to upload');
    } finally {
      setUploadingStamp(false);
    }
  };

  // Save profile details
  const handleSaveProfile = async () => {
    setSavingProfile(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/update-profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileFormData),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Profile updated!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to update');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to save');
    } finally {
      setSavingProfile(false);
    }
  };

  // Delete upload
  const handleDeleteUpload = async (uploadType) => {
    if (!window.confirm(`Delete your ${uploadType.replace('_', ' ')}?`)) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/user/delete-upload/${uploadType}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Deleted!');
        loadUserProfile();
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to delete');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to delete');
    }
  };

  // Load applications
  const loadApplications = async () => {
    setLoadingApplications(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/applications`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setApplications(data.applications || []);
        setNewAppsCount(data.new_count || 0);
      } else {
        throw new Error('Failed to load applications');
      }
    } catch (error) {
      console.error('Error loading applications:', error);
      toast.error('Failed to load applications');
    } finally {
      setLoadingApplications(false);
    }
  };

  // Load applications count for notification badge
  const loadApplicationsCount = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/applications/count`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setNewAppsCount(data.new_count || 0);
      }
    } catch (error) {
      console.error('Error loading applications count:', error);
    }
  };

  // Mark application as read
  const markAsRead = async (app) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      await authenticatedFetch(`${backendUrl}/api/siso/application/${app.id}/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: app.source }),
        credentials: 'include'
      });
      
      // Update local state
      setApplications(prev => prev.map(a => 
        a.id === app.id ? { ...a, siso_read_status: 'read' } : a
      ));
      setNewAppsCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // View application details
  const viewApplication = (app) => {
    setSelectedApplication(app);
    setShowApplicationDetail(true);
    if (app.siso_read_status !== 'read') {
      markAsRead(app);
    }
  };

  // Handle approve action
  const handleApprove = async (app) => {
    setProcessingAction(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/application/${app.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'approve',
          source: app.source,
          comment: 'Approved by SISO'
        }),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Application approved successfully!');
        loadApplications();
        setShowApplicationDetail(false);
        setSelectedApplication(null);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to approve');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to approve application');
    } finally {
      setProcessingAction(false);
    }
  };

  // Handle disapprove action
  const handleDisapprove = async () => {
    if (!disapproveComment.trim()) {
      toast.error('Please provide a reason for disapproval');
      return;
    }

    setProcessingAction(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/application/${selectedApplication.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'disapprove',
          source: selectedApplication.source,
          comment: disapproveComment
        }),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Application disapproved');
        loadApplications();
        setShowApplicationDetail(false);
        setSelectedApplication(null);
        setShowDisapproveModal(false);
        setDisapproveComment('');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to disapprove');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to disapprove application');
    } finally {
      setProcessingAction(false);
    }
  };

  // Fetch application support fee
  const fetchApplicationSupportFee = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await fetch(`${backendUrl}/api/application-support-fee/settings`);
      if (response.ok) {
        const data = await response.json();
        setApplicationSupportFee(data.fee_amount || 0);
      }
    } catch (error) {
      console.error('Error fetching application support fee:', error);
    }
  };

  // Start escalation flow
  const startEscalation = (app) => {
    setEscalatingApp(app);
    setEscalateMemoText('');
    setFormattedDirectorMemo('');
    setShowMemoPreview(false);
    setIsEditingMemo(false);
    setEscalationPDFReady(false);
    fetchApplicationSupportFee();
  };

  // Generate combined escalation PDF
  const generateEscalationPDF = async () => {
    if (!formattedDirectorMemo) {
      toast.error('Please generate the memo first');
      return;
    }

    setGeneratingEscalationPDF(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/generate-escalation-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: escalatingApp.id,
          application_source: escalatingApp.source,
          siso_memo: formattedDirectorMemo
        }),
        credentials: 'include'
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `escalation_${escalatingApp.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        setEscalationPDFReady(true);
        toast.success('PDF generated successfully!');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate PDF');
      }
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error(error.message || 'Failed to generate PDF');
    } finally {
      setGeneratingEscalationPDF(false);
    }
  };

  // Handle Pay to Submit - initiates Paystack payment
  const handlePayToSubmit = async () => {
    if (!escalatingApp) return;

    if (applicationSupportFee <= 0) {
      toast.error('Application support fee not configured. Contact admin.');
      return;
    }

    setProcessingPayment(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      
      // Get the director for this application
      const directorResponse = await authenticatedFetch(`${backendUrl}/api/siso/get-district-director`, {
        method: 'GET',
        credentials: 'include'
      });

      if (!directorResponse.ok) {
        throw new Error('Could not find district director');
      }

      const directorData = await directorResponse.json();
      
      // Initialize payment
      const paymentResponse = await authenticatedFetch(`${backendUrl}/api/siso/initiate-escalation-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          application_id: escalatingApp.id,
          application_source: escalatingApp.source,
          siso_memo: formattedDirectorMemo,
          director_id: directorData.director_id,
          payer_id: escalatingApp.source === 'teacher' ? escalatingApp.teacher_id : escalatingApp.headteacher_id,
          payer_email: escalatingApp.source === 'teacher' ? escalatingApp.teacher_email : escalatingApp.headteacher_email
        }),
        credentials: 'include'
      });

      if (paymentResponse.ok) {
        const paymentData = await paymentResponse.json();
        
        // New flow: Backend sends notification to applicant instead of redirecting SISO
        if (paymentData.status === 'success' && paymentData.payer_notified) {
          toast.success(paymentData.message || 'Payment link has been sent to the applicant\'s notification inbox');
          // Close the modal and refresh applications list
          setEscalatingApp(null);
          setFormattedDirectorMemo('');
          setEscalateMemoText('');
          setShowMemoPreview(false);
          loadApplications(); // Refresh the list
        } else if (paymentData.payment_url) {
          // Legacy flow: Redirect to Paystack payment page (keeping for backwards compatibility)
          toast.success('Redirecting to payment...');
          window.location.href = paymentData.payment_url;
        } else {
          throw new Error('Payment initialization failed');
        }
      } else {
        const error = await paymentResponse.json();
        throw new Error(error.detail || 'Failed to initiate payment');
      }
    } catch (error) {
      console.error('Error initiating payment:', error);
      toast.error(error.message || 'Failed to initiate payment');
    } finally {
      setProcessingPayment(false);
    }
  };

  // Generate director memo
  const generateDirectorMemo = async () => {
    if (!escalateMemoText.trim()) {
      toast.error('Please enter key facts for the memo');
      return;
    }

    setGeneratingMemo(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/generate-director-memo`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          plain_text: escalateMemoText,
          applicant_name: escalatingApp.source === 'teacher' 
            ? escalatingApp.teacher_name 
            : escalatingApp.headteacher_name,
          applicant_role: escalatingApp.source === 'teacher' ? 'Teacher' : 'Headteacher',
          application_type: escalatingApp.application_type,
          school_name: escalatingApp.school_name
        }),
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setFormattedDirectorMemo(data.formatted_memo);
        const htmlContent = convertStructuredTextToHtml(data.formatted_memo);
        setEditedMemoHtml(htmlContent);
        setShowMemoPreview(true);
        setMemoSaved(false);
        toast.success('Memo generated — edit below, then save.');
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to generate memo');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to generate memo');
    } finally {
      setGeneratingMemo(false);
    }
  };

  // Complete escalation to director
  const completeEscalation = async () => {
    setProcessingAction(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
      const response = await authenticatedFetch(`${backendUrl}/api/siso/application/${escalatingApp.id}/action`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'escalate_to_director',
          source: escalatingApp.source,
          comment: escalateMemoText,
          formatted_memo: formattedDirectorMemo
        }),
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Application escalated to Director successfully!');
        loadApplications();
        setEscalatingApp(null);
        setEscalateMemoText('');
        setFormattedDirectorMemo('');
        setShowMemoPreview(false);
        setShowApplicationDetail(false);
        setSelectedApplication(null);
      } else {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to escalate');
      }
    } catch (error) {
      toast.error(error.message || 'Failed to escalate application');
    } finally {
      setProcessingAction(false);
    }
  };

  // Print memo
  const printMemo = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Director Escalation Memo</title>
          <style>
            body { font-family: 'Times New Roman', serif; padding: 40px; line-height: 1.6; }
            .right { text-align: right; }
            .center { text-align: center; }
            .heading { text-decoration: underline; font-weight: bold; text-transform: uppercase; }
            pre { white-space: pre-wrap; font-family: inherit; margin: 0; }
          </style>
        </head>
        <body>
          <pre>${formattedDirectorMemo}</pre>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      'submitted': { color: 'bg-blue-100 text-blue-800', label: 'Submitted' },
      'escalated_to_siso': { color: 'bg-purple-100 text-purple-800', label: 'Received' },
      'approved_by_siso': { color: 'bg-green-100 text-green-800', label: 'Approved' },
      'disapproved_by_siso': { color: 'bg-red-100 text-red-800', label: 'Disapproved' },
      'escalated_to_director': { color: 'bg-indigo-100 text-indigo-800', label: 'Sent to Director' }
    };
    const config = statusConfig[status] || { color: 'bg-gray-100 text-gray-800', label: status };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>{config.label}</span>;
  };

  useEffect(() => {
    loadDashboard();
    loadSISOSettings();
    loadApplicationsCount();
    loadUserProfile(); // Load profile on initial load for signature/stamp in memo preview
    loadDDConnectionStatus(); // Load DD-Supervision connection status
    
    // Handle payment callback from Paystack redirect
    const urlParams = new URLSearchParams(window.location.search);
    const paymentStatus = urlParams.get('payment');
    const paymentRef = urlParams.get('ref');
    
    if (paymentStatus) {
      if (paymentStatus === 'success') {
        toast.success('Payment successful! Application has been escalated to the Director.');
        // Reload applications to show updated status
        loadApplications();
      } else if (paymentStatus === 'failed') {
        toast.error('Payment was not completed. Please try again.');
      } else if (paymentStatus === 'error') {
        toast.error('An error occurred during payment. Please contact support.');
      }
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-4 flex items-center justify-center">
        <Loader2 className="h-10 w-10 animate-spin text-green-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-3 sm:p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        
        {/* Header - Mobile Responsive */}
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-start">
            <div className="flex-1 min-w-0">
              <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900 truncate">
                {user?.sector === 'private' ? 'Proprietor(ress) Dashboard' : 'SISO Dashboard'}
              </h1>
              <div className="flex items-center gap-2 flex-wrap">
                <p className="text-xs sm:text-sm text-gray-600 truncate">
                  {dashboardData?.user?.circuit_name} • {dashboardData?.user?.district_name}
                </p>
                {dataFromCache && (
                  <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                    <Clock className="h-3 w-3 mr-1" />
                    Cached
                  </Badge>
                )}
                {isOffline && (
                  <Badge variant="outline" className="text-xs bg-red-50 text-red-700 border-red-300">
                    Offline
                  </Badge>
                )}
              </div>
            </div>
            <Button 
              onClick={handleLogout} 
              variant="outline" 
              size="sm"
              className="text-red-600 border-red-600 hover:bg-red-50 shrink-0"
            >
              <LogOut className="h-4 w-4" />
              <span className="hidden sm:inline ml-1">Logout</span>
            </Button>
          </div>
          
          {/* Action Button */}
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
                <Plus className="h-4 w-4 mr-2" />
                Add School
              </Button>
            </DialogTrigger>
            <DialogContent className="w-[95vw] max-w-md mx-auto">
              <DialogHeader>
                <DialogTitle>Create School Folder</DialogTitle>
                <DialogDescription>Add a school to your circuit.</DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Input
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="Enter school name"
                  onKeyDown={(e) => e.key === 'Enter' && createSchoolFolder()}
                />
              </div>
              <DialogFooter className="flex-col sm:flex-row gap-2">
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)} className="w-full sm:w-auto">Cancel</Button>
                <Button onClick={createSchoolFolder} disabled={creating} className="bg-green-600 hover:bg-green-700 w-full sm:w-auto">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Support Action Modal */}
          <Dialog open={showSupportActionModal} onOpenChange={setShowSupportActionModal}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-orange-700">
                  <HeartHandshake className="h-5 w-5" />
                  Create Support Action
                </DialogTitle>
                <DialogDescription>
                  Provide improvement support for {supportActionTeacher?.name}. This action will be logged and visible to both the teacher and headteacher.
                </DialogDescription>
              </DialogHeader>

              {loadingSupportPerformance ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Performance Summary */}
                  {supportActionPerformance && (
                    <div className="bg-gray-50 rounded-lg p-4 border">
                      <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                        <Target className="h-4 w-4 text-gray-600" />
                        Performance Overview (Week {supportActionPerformance.current_week})
                      </h4>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-blue-50 p-3 rounded-lg">
                          <p className="text-xs text-blue-600 font-medium">Lesson Notes</p>
                          <p className="text-xl font-bold text-blue-800">
                            {supportActionPerformance.lesson_notes?.total_vetted || 0}/{supportActionPerformance.lesson_notes?.total_submitted || 0}
                          </p>
                          <p className="text-xs text-blue-600">Vetted/Submitted</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg">
                          <p className="text-xs text-green-600 font-medium">Work Output</p>
                          <p className="text-xl font-bold text-green-800">
                            {supportActionPerformance.work_output?.weeks_verified || 0}/{supportActionPerformance.work_output?.weeks_with_data || 0}
                          </p>
                          <p className="text-xs text-green-600">Verified/Submitted</p>
                        </div>
                      </div>
                      
                      {/* Weekly Breakdown */}
                      {supportActionPerformance.lesson_notes?.weekly_breakdown && (
                        <div className="mt-3">
                          <p className="text-xs font-medium text-gray-600 mb-2">Weekly Lesson Notes</p>
                          <div className="flex gap-1 flex-wrap">
                            {supportActionPerformance.lesson_notes.weekly_breakdown.map(week => (
                              <div 
                                key={week.week} 
                                className={`px-2 py-1 rounded text-xs ${
                                  week.vetted > 0 ? 'bg-green-100 text-green-700' : 
                                  week.submitted > 0 ? 'bg-amber-100 text-amber-700' : 
                                  'bg-gray-100 text-gray-500'
                                }`}
                              >
                                W{week.week}: {week.vetted}/{week.submitted}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Previous Support Actions */}
                  {supportActionPerformance?.previous_support_actions?.length > 0 && (
                    <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                      <p className="text-xs font-medium text-amber-800 mb-2 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" />
                        Previous Support Actions ({supportActionPerformance.previous_support_actions.length})
                      </p>
                      <div className="space-y-1">
                        {supportActionPerformance.previous_support_actions.slice(0, 3).map(action => (
                          <div key={action.id} className="text-xs bg-white p-2 rounded">
                            <span className="font-medium">{action.action_title}</span>
                            <span className={`ml-2 px-1.5 py-0.5 rounded text-[10px] ${
                              action.status === 'completed' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                            }`}>
                              {action.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Support Action Form */}
                  <div className="space-y-4">
                    {/* Action Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Support Area</label>
                      <select
                        value={supportActionForm.action_type}
                        onChange={(e) => setSupportActionForm(prev => ({ ...prev, action_type: e.target.value }))}
                        className="w-full p-2 border rounded-lg text-sm"
                      >
                        <option value="lesson_notes_support">Lesson Notes Improvement</option>
                        <option value="work_output_support">Work Output Enhancement</option>
                        <option value="teaching_methodology">Teaching Methodology</option>
                        <option value="classroom_management">Classroom Management</option>
                        <option value="general_support">General Professional Support</option>
                      </select>
                    </div>

                    {/* Action Title */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Action Title *</label>
                      <Input
                        value={supportActionForm.action_title}
                        onChange={(e) => setSupportActionForm(prev => ({ ...prev, action_title: e.target.value }))}
                        placeholder="e.g., Weekly Lesson Note Review Support"
                        className="text-sm"
                      />
                    </div>

                    {/* Action Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                      <textarea
                        value={supportActionForm.action_description}
                        onChange={(e) => setSupportActionForm(prev => ({ ...prev, action_description: e.target.value }))}
                        placeholder="Describe the purpose and goals of this support action..."
                        className="w-full p-2 border rounded-lg text-sm min-h-[80px]"
                        rows={3}
                      />
                    </div>

                    {/* Recommended Actions */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-1">
                        <ListChecks className="h-4 w-4" />
                        Recommended Actions
                      </label>
                      <div className="space-y-2">
                        {supportActionForm.recommended_actions.map((action, index) => (
                          <div key={index} className="flex gap-2">
                            <Input
                              value={action}
                              onChange={(e) => updateRecommendedAction(index, e.target.value)}
                              placeholder={`Action ${index + 1}: e.g., Submit lesson notes by Wednesday`}
                              className="text-sm flex-1"
                            />
                            {supportActionForm.recommended_actions.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeRecommendedAction(index)}
                                className="text-red-500 hover:text-red-700"
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        ))}
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={addRecommendedAction}
                          className="text-xs"
                        >
                          <Plus className="h-3 w-3 mr-1" /> Add Recommendation
                        </Button>
                      </div>
                    </div>

                    {/* Follow-up Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Follow-up Date (Optional)</label>
                      <Input
                        type="date"
                        value={supportActionForm.follow_up_date}
                        onChange={(e) => setSupportActionForm(prev => ({ ...prev, follow_up_date: e.target.value }))}
                        className="text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowSupportActionModal(false)}>
                  Cancel
                </Button>
                <Button 
                  onClick={submitSupportAction}
                  disabled={submittingSupportAction || loadingSupportPerformance}
                  className="bg-orange-500 hover:bg-orange-600"
                >
                  {submittingSupportAction ? (
                    <><Loader2 className="h-4 w-4 animate-spin mr-2" />Creating...</>
                  ) : (
                    <><HeartHandshake className="h-4 w-4 mr-2" />Create Support Action</>
                  )}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Director Connection Card */}
        <Card className={`border-2 ${ddConnectionStatus?.connected ? 'border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50' : 'border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50'}`}>
          <CardContent className="p-3 sm:p-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg ${ddConnectionStatus?.connected ? 'bg-purple-100' : 'bg-amber-100'}`}>
                  <Network className={`h-5 w-5 ${ddConnectionStatus?.connected ? 'text-purple-600' : 'text-amber-600'}`} />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Director Connection</h3>
                  {ddConnectionStatus?.connected ? (
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="bg-purple-100 text-purple-700">
                        <CheckCircle className="h-3 w-3 mr-1" />
                        Connected
                      </Badge>
                      <span className="text-sm text-gray-600">
                        {ddConnectionStatus.director_name || ddConnectionStatus.dd_supervision_name} • {ddConnectionStatus.district_name}
                      </span>
                    </div>
                  ) : (
                    <p className="text-sm text-amber-700">
                      Not connected. Get a connect token from your District Director.
                    </p>
                  )}
                </div>
              </div>
              
              <div className="flex gap-2 w-full sm:w-auto">
                {ddConnectionStatus?.connected ? (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={disconnectFromDD}
                    className="flex items-center gap-2 text-red-600 border-red-200 hover:bg-red-50 w-full sm:w-auto"
                  >
                    <Unlink className="h-4 w-4" />
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => setShowConnectModal(true)}
                    className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto"
                  >
                    <Link className="h-4 w-4 mr-2" />
                    Connect to Director
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Connect to Director Modal */}
        {showConnectModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
              <div className="p-6 border-b">
                <h2 className="text-xl font-semibold flex items-center gap-2">
                  <Link className="h-5 w-5 text-purple-600" />
                  Connect to Director
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Enter the connect token provided by your District Director
                </p>
              </div>
              
              <div className="p-6 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Connect Token
                  </label>
                  <Input
                    type="text"
                    placeholder="Paste the connect token here"
                    value={connectTokenInput}
                    onChange={(e) => setConnectTokenInput(e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    <strong>Note:</strong> This token links your SISO account to the District Director. 
                    Once connected, the Director can view your circuit data and school reports.
                  </p>
                </div>
              </div>
              
              <div className="p-6 border-t bg-gray-50 flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowConnectModal(false);
                    setConnectTokenInput('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  onClick={connectToDDSupervision}
                  disabled={connectingToDD || !connectTokenInput.trim()}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  {connectingToDD ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Link className="h-4 w-4 mr-2" />
                  )}
                  Connect
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Stats Overview - 5 items: Schools, Connected, Teachers, Headteachers, Reports */}
        <div className="grid grid-cols-5 gap-1.5 sm:gap-2 md:gap-3">
          <Card className="bg-white">
            <CardContent className="p-1.5 sm:p-2 md:p-3">
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                <div className="p-1 sm:p-1.5 bg-blue-100 rounded-lg shrink-0">
                  <School className="h-3 w-3 sm:h-4 sm:w-4 text-blue-600" />
                </div>
                <div className="text-center sm:text-left min-w-0">
                  <p className="text-sm sm:text-lg font-bold">{dashboardData?.stats?.total_schools || 0}</p>
                  <p className="text-[8px] sm:text-[10px] text-gray-500">Schools</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-1.5 sm:p-2 md:p-3">
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                <div className="p-1 sm:p-1.5 bg-green-100 rounded-lg shrink-0">
                  <CheckCircle className="h-3 w-3 sm:h-4 sm:w-4 text-green-600" />
                </div>
                <div className="text-center sm:text-left min-w-0">
                  <p className="text-sm sm:text-lg font-bold">{dashboardData?.stats?.connected_schools || 0}</p>
                  <p className="text-[8px] sm:text-[10px] text-gray-500">Connected</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-1.5 sm:p-2 md:p-3">
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                <div className="p-1 sm:p-1.5 bg-purple-100 rounded-lg shrink-0">
                  <Users className="h-3 w-3 sm:h-4 sm:w-4 text-purple-600" />
                </div>
                <div className="text-center sm:text-left min-w-0">
                  <p className="text-sm sm:text-lg font-bold">{dashboardData?.stats?.total_teachers || 0}</p>
                  <p className="text-[8px] sm:text-[10px] text-gray-500">Teachers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-1.5 sm:p-2 md:p-3">
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                <div className="p-1 sm:p-1.5 bg-indigo-100 rounded-lg shrink-0">
                  <UserCheck className="h-3 w-3 sm:h-4 sm:w-4 text-indigo-600" />
                </div>
                <div className="text-center sm:text-left min-w-0">
                  <p className="text-sm sm:text-lg font-bold">{dashboardData?.stats?.connected_schools || 0}</p>
                  <p className="text-[8px] sm:text-[10px] text-gray-500">Headteachers</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-white">
            <CardContent className="p-1.5 sm:p-2 md:p-3">
              <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-2">
                <div className="p-1 sm:p-1.5 bg-amber-100 rounded-lg shrink-0">
                  <FileText className="h-3 w-3 sm:h-4 sm:w-4 text-amber-600" />
                </div>
                <div className="text-center sm:text-left min-w-0">
                  <p className="text-sm sm:text-lg font-bold">{dashboardData?.stats?.schools_with_reports || 0}</p>
                  <p className="text-[8px] sm:text-[10px] text-gray-500">Reports</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* PWA Install Prompt */}
        <InstallPWA />

        {/* Main Content Tabs */}
        <Tabs defaultValue="schools" className="w-full">
          <TabsList className="grid w-full grid-cols-3 sm:grid-cols-5 h-auto gap-1">
            <TabsTrigger value="schools" className="text-xs sm:text-sm py-2">
              <School className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Schools
            </TabsTrigger>
            <TabsTrigger 
              value="supervision" 
              className="text-xs sm:text-sm py-2"
              onClick={() => {
                if (!supervisionData) {
                  loadSupervisionData();
                }
              }}
              data-testid="supervision-tab"
            >
              <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Monitor
            </TabsTrigger>
            <TabsTrigger 
              value="applications" 
              className="text-xs sm:text-sm py-2 relative"
              onClick={loadApplications}
              data-testid="applications-tab"
            >
              <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Applications
              {newAppsCount > 0 && (
                <span className="absolute -top-1 -right-1 h-4 w-4 bg-red-500 rounded-full flex items-center justify-center text-[9px] text-white font-bold animate-pulse">
                  {newAppsCount > 99 ? '99+' : newAppsCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="reports" className="text-xs sm:text-sm py-2">
              <FileDown className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Reports
            </TabsTrigger>
            <TabsTrigger 
              value="profile" 
              className="text-xs sm:text-sm py-2"
              onClick={loadUserProfile}
              data-testid="profile-tab"
            >
              <Users className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Profile
            </TabsTrigger>
            <TabsTrigger value="promotion_exams" className="text-xs sm:text-sm py-2">
              <GraduationCap className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
              Promotion Exams
            </TabsTrigger>
          </TabsList>
          <TabsContent value="schools" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            {dashboardData?.folders?.length === 0 ? (
              <Card>
                <CardContent className="py-8 sm:py-12 text-center">
                  <School className="h-12 w-12 sm:h-16 sm:w-16 mx-auto text-gray-300 mb-3 sm:mb-4" />
                  <h3 className="text-base sm:text-lg font-medium text-gray-600 mb-2">No Schools Yet</h3>
                  <Button onClick={() => setIsCreateDialogOpen(true)} className="bg-green-600 hover:bg-green-700">
                    <Plus className="h-4 w-4 mr-2" />
                    Add First School
                  </Button>
                </CardContent>
              </Card>
            ) : (
              dashboardData?.folders?.map((folder) => (
                <Card key={folder.id} className={`border-l-4 ${folder.headteacher_id ? 'border-l-green-500' : 'border-l-amber-500'}`}>
                  <CardContent className="p-3 sm:p-4">
                    {editingFolderId === folder.id ? (
                      // Edit Mode - Mobile Friendly
                      <div className="space-y-3">
                        <div className="space-y-2">
                          <div>
                            <label className="text-xs text-gray-500">School Name</label>
                            <Input
                              value={editSchoolName}
                              onChange={(e) => setEditSchoolName(e.target.value)}
                              className="mt-1 h-9"
                            />
                          </div>
                          <div>
                            <label className="text-xs text-gray-500">Headteacher Name</label>
                            <Input
                              value={editHeadteacherName}
                              onChange={(e) => setEditHeadteacherName(e.target.value)}
                              className="mt-1 h-9"
                              placeholder="Optional"
                            />
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => saveEdit(folder.id)} disabled={saving} className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none">
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 mr-1" /> Save</>}
                          </Button>
                          <Button size="sm" variant="outline" onClick={cancelEditing} className="flex-1 sm:flex-none">
                            <X className="h-4 w-4 mr-1" /> Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      // View Mode - Mobile Friendly
                      <div className="space-y-3">
                        {/* Header Row */}
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-sm sm:text-base truncate">{folder.school_name}</h3>
                              {folder.headteacher_id ? (
                                <Badge className="bg-green-100 text-green-800 text-[10px] sm:text-xs shrink-0">
                                  <CheckCircle className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5" />Connected
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="text-amber-600 border-amber-500 text-[10px] sm:text-xs shrink-0">
                                  Pending
                                </Badge>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-1 truncate">
                              {folder.headteacher_name || 'Not connected'} • {folder.teacher_count || 0} teachers
                            </p>
                          </div>
                          
                          {/* Action Buttons */}
                          <div className="flex gap-1 shrink-0">
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0" onClick={() => startEditing(folder)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            {folder.has_reports && (
                              <Button 
                                size="sm" 
                                variant="ghost" 
                                className="h-8 w-8 p-0 text-blue-600"
                                onClick={() => generateSchoolReport(folder.id, folder.school_name)}
                                disabled={generatingReport === folder.id}
                              >
                                {generatingReport === folder.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-600" onClick={() => deleteFolder(folder.id, folder.school_name)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>

                        {/* Token Row */}
                        <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-2">
                          <span className="text-[10px] sm:text-xs text-gray-500 shrink-0">Token:</span>
                          <code className="text-[10px] sm:text-xs bg-white px-2 py-1 rounded font-mono flex-1 truncate border">
                            {folder.connect_token}
                          </code>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => copyToken(folder.connect_token)}>
                            <Copy className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="h-7 w-7 p-0 shrink-0" onClick={() => regenerateToken(folder.id)}>
                            <RefreshCw className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Supervision Tab - Real-time Monitoring */}
          <TabsContent value="supervision" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            {loadingSupervision ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-600 mb-4" />
                  <p className="text-gray-600">Loading supervision data...</p>
                </CardContent>
              </Card>
            ) : !supervisionData ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Eye className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <h3 className="text-lg font-medium text-gray-600 mb-2">Real-time Monitoring</h3>
                  <p className="text-sm text-gray-500 mb-4">View schools, headteachers, and teachers under your supervision</p>
                  <Button onClick={loadSupervisionData} className="bg-blue-600 hover:bg-blue-700">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Load Supervision Data
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Refresh Button */}
                <div className="flex justify-end">
                  <Button size="sm" variant="outline" onClick={loadSupervisionData} disabled={loadingSupervision}>
                    <RefreshCw className={`h-4 w-4 mr-1 ${loadingSupervision ? 'animate-spin' : ''}`} />
                    Refresh
                  </Button>
                </div>

                {/* Schools List with Collapsible Teachers */}
                <Card>
                  <CardHeader className="p-3 pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Schools Under Supervision
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Click on a school to view headteacher and teachers. Click on a teacher for details.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 pt-0">
                    {supervisionData?.schools?.length === 0 ? (
                      <p className="text-gray-500 text-sm text-center py-4">No schools added yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {supervisionData?.schools?.map((school) => (
                          <div key={school.id} className="border rounded-lg overflow-hidden">
                            {/* School Header - Clickable */}
                            <button
                              onClick={() => toggleSchoolExpansion(school.id)}
                              className={`w-full p-3 flex items-center justify-between text-left transition-colors ${
                                expandedSchools[school.id] 
                                  ? 'bg-blue-50 border-b border-blue-200' 
                                  : 'bg-gray-50 hover:bg-gray-100'
                              }`}
                              data-testid={`school-${school.id}`}
                            >
                              <div className="flex items-center gap-2 min-w-0 flex-1">
                                {expandedSchools[school.id] ? (
                                  <ChevronDown className="h-4 w-4 text-blue-600 shrink-0" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-gray-500 shrink-0" />
                                )}
                                <School className="h-4 w-4 text-blue-600 shrink-0" />
                                <span className="font-medium text-sm truncate">{school.school_name}</span>
                                {school.connected ? (
                                  <Badge className="bg-green-100 text-green-700 text-[10px] shrink-0">
                                    <CheckCircle className="h-2.5 w-2.5 mr-0.5" />Connected
                                  </Badge>
                                ) : (
                                  <Badge variant="outline" className="text-amber-600 border-amber-400 text-[10px] shrink-0">
                                    Pending
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-gray-500 shrink-0">
                                <span>{school.stats?.total_teachers || 0} teachers</span>
                              </div>
                            </button>

                            {/* Expanded School Content */}
                            {expandedSchools[school.id] && (
                              <div className="p-3 bg-white">
                                {!school.connected ? (
                                  <p className="text-gray-500 text-sm text-center py-2">
                                    School not yet connected to a headteacher
                                  </p>
                                ) : (
                                  <div className="space-y-3">
                                    {/* Headteacher (Listed First) */}
                                    {school.headteacher && (
                                      <div className="p-2 bg-purple-50 rounded-lg border border-purple-200">
                                        <div className="flex items-center gap-2">
                                          {school.headteacher.profile_picture ? (
                                            <img 
                                              src={`${process.env.REACT_APP_BACKEND_URL}${school.headteacher.profile_picture}`} 
                                              alt={school.headteacher.name}
                                              className="h-10 w-10 rounded-full object-cover border-2 border-purple-300"
                                            />
                                          ) : (
                                            <div className="p-1.5 bg-purple-100 rounded-full">
                                              <UserCheck className="h-4 w-4 text-purple-600" />
                                            </div>
                                          )}
                                          <div className="min-w-0 flex-1">
                                            <p className="font-medium text-sm text-purple-800">{school.headteacher.name}</p>
                                            <p className="text-[10px] text-purple-600">{school.headteacher.email}</p>
                                          </div>
                                          <Badge className="bg-purple-600 text-white text-[10px]">Headteacher</Badge>
                                        </div>
                                      </div>
                                    )}

                                    {/* Teachers List */}
                                    {school.teachers?.length === 0 ? (
                                      <p className="text-gray-500 text-xs text-center py-2">No teachers connected yet</p>
                                    ) : (
                                      <div className="space-y-1">
                                        <p className="text-xs font-medium text-gray-600 mb-1">Teachers ({school.teachers?.length || 0})</p>
                                        {school.teachers?.map((teacher) => (
                                          <button
                                            key={teacher.id}
                                            onClick={() => loadTeacherDetails(teacher.id)}
                                            className={`w-full p-2 rounded-lg flex items-center justify-between text-left transition-colors ${
                                              selectedTeacher === teacher.id 
                                                ? 'bg-blue-100 border border-blue-300' 
                                                : 'bg-gray-50 hover:bg-gray-100 border border-transparent'
                                            }`}
                                            data-testid={`teacher-${teacher.id}`}
                                          >
                                            <div className="flex items-center gap-2 min-w-0">
                                              {teacher.profile_picture ? (
                                                <img 
                                                  src={`${process.env.REACT_APP_BACKEND_URL}${teacher.profile_picture}`} 
                                                  alt={teacher.name}
                                                  className="h-8 w-8 rounded-full object-cover border border-gray-300"
                                                />
                                              ) : (
                                                <User className="h-4 w-4 text-gray-500 shrink-0" />
                                              )}
                                              <div className="min-w-0">
                                                <p className="font-medium text-xs truncate">{teacher.name}</p>
                                                <p className="text-[10px] text-gray-500 truncate">{teacher.class_or_subject || 'N/A'}</p>
                                              </div>
                                            </div>
                                            {selectedTeacher === teacher.id && loadingTeacherDetails ? (
                                              <Loader2 className="h-3 w-3 animate-spin text-blue-600" />
                                            ) : (
                                              <ChevronRight className="h-4 w-4 text-gray-400" />
                                            )}
                                          </button>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Teacher Details Panel */}
                {selectedTeacher && teacherDetails && (
                  <Card className="border-blue-200 bg-blue-50/30">
                    <CardHeader className="p-3 pb-2">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm flex items-center gap-2">
                          <User className="h-4 w-4 text-blue-600" />
                          Teacher Details
                        </CardTitle>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          onClick={() => { setSelectedTeacher(null); setTeacherDetails(null); }}
                          className="h-6 px-2"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="p-3 pt-0 space-y-4">
                      {/* Teacher Info */}
                      <div className="bg-white p-3 rounded-lg border">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                          <User className="h-4 w-4" /> Profile
                        </h4>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">Name:</span>
                            <p className="font-medium">{teacherDetails.teacher?.name}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Email:</span>
                            <p className="font-medium truncate">{teacherDetails.teacher?.email}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Class/Subject:</span>
                            <p className="font-medium">{teacherDetails.folder?.class_or_subject || 'N/A'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Level:</span>
                            <p className="font-medium capitalize">{teacherDetails.folder?.education_level || 'N/A'}</p>
                          </div>
                        </div>
                      </div>

                      {/* Work Output - Moved to TOP */}
                      {teacherDetails.work_output && (
                        <div className="bg-white p-3 rounded-lg border">
                          <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                            <Briefcase className="h-4 w-4" /> Work Output (Stated vs Verified)
                          </h4>
                          {Object.keys(teacherDetails.work_output.work_output || {}).length === 0 ? (
                            <p className="text-xs text-gray-500">No work output recorded</p>
                          ) : (
                            <div className="space-y-2">
                              {Object.entries(teacherDetails.work_output.work_output || {}).map(([subject, weeks]) => (
                                <div key={subject} className="border rounded p-2">
                                  <p className="text-xs font-medium text-gray-700 mb-1">{subject}</p>
                                  <div className="flex flex-wrap gap-1">
                                    {Object.entries(weeks || {}).map(([week, value]) => {
                                      const verification = teacherDetails.work_output.verification?.[subject]?.[week];
                                      const isVerified = verification?.status === 'compliant';
                                      return (
                                        <span
                                          key={week}
                                          className={`px-1.5 py-0.5 rounded text-[10px] ${
                                            isVerified 
                                              ? 'bg-green-100 text-green-700' 
                                              : 'bg-amber-100 text-amber-700'
                                          }`}
                                          title={isVerified ? 'Verified by Headteacher' : 'Not yet verified'}
                                        >
                                          W{week}: {value} {isVerified ? '✓' : '○'}
                                        </span>
                                      );
                                    })}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Lesson Notes Section - Grouped by Week */}
                      <div className="bg-white p-3 rounded-lg border">
                        <h4 className="font-medium text-sm mb-2 flex items-center gap-1">
                          <BookOpen className="h-4 w-4" /> Lesson Note Submissions
                        </h4>
                        
                        {/* Overall Stats */}
                        <div className="grid grid-cols-3 gap-2 mb-3">
                          <div className="bg-blue-50 p-2 rounded-lg text-center">
                            <p className="text-lg font-bold text-blue-600">{teacherDetails.stats?.total_submissions || 0}</p>
                            <p className="text-[10px] text-gray-500">Total</p>
                          </div>
                          <div className="bg-green-50 p-2 rounded-lg text-center">
                            <p className="text-lg font-bold text-green-600">{teacherDetails.stats?.vetted_submissions || 0}</p>
                            <p className="text-[10px] text-gray-500">Vetted</p>
                          </div>
                          <div className="bg-amber-50 p-2 rounded-lg text-center">
                            <p className="text-lg font-bold text-amber-600">{teacherDetails.stats?.pending_submissions || 0}</p>
                            <p className="text-[10px] text-gray-500">Pending</p>
                          </div>
                        </div>

                        {/* Submissions Grouped by Week */}
                        {teacherDetails.submissions?.length === 0 ? (
                          <p className="text-xs text-gray-500">No lesson notes submitted yet</p>
                        ) : (
                          <div className="space-y-2 max-h-64 overflow-y-auto">
                            {(() => {
                              // Group submissions by week
                              const groupedByWeek = teacherDetails.submissions?.reduce((acc, sub) => {
                                const week = sub.week || 'Unknown';
                                if (!acc[week]) {
                                  acc[week] = [];
                                }
                                acc[week].push(sub);
                                return acc;
                              }, {});
                              
                              // Sort weeks numerically
                              const sortedWeeks = Object.keys(groupedByWeek || {}).sort((a, b) => {
                                const numA = parseInt(a) || 0;
                                const numB = parseInt(b) || 0;
                                return numA - numB;
                              });
                              
                              return sortedWeeks.map((week) => {
                                const weekSubmissions = groupedByWeek[week];
                                const weekVetted = weekSubmissions.filter(s => s.vetted).length;
                                const weekPending = weekSubmissions.length - weekVetted;
                                const isExpanded = expandedSubmissionWeeks[week];
                                
                                return (
                                  <div key={week} className="border rounded-lg overflow-hidden">
                                    {/* Week Header - Clickable */}
                                    <button
                                      onClick={() => setExpandedSubmissionWeeks(prev => ({
                                        ...prev,
                                        [week]: !prev[week]
                                      }))}
                                      className={`w-full p-2 flex items-center justify-between text-left transition-colors ${
                                        isExpanded 
                                          ? 'bg-blue-50 border-b border-blue-200' 
                                          : 'bg-gray-50 hover:bg-gray-100'
                                      }`}
                                    >
                                      <div className="flex items-center gap-2">
                                        {isExpanded ? (
                                          <ChevronDown className="h-3 w-3 text-blue-600" />
                                        ) : (
                                          <ChevronRight className="h-3 w-3 text-gray-500" />
                                        )}
                                        <Calendar className="h-3 w-3 text-gray-500" />
                                        <span className="font-medium text-xs">Week {week}</span>
                                        <span className="text-[10px] text-gray-500">({weekSubmissions.length} notes)</span>
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">{weekVetted} ✓</span>
                                        {weekPending > 0 && (
                                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">{weekPending} ○</span>
                                        )}
                                      </div>
                                    </button>
                                    
                                    {/* Week Content - Expanded */}
                                    {isExpanded && (
                                      <div className="p-2 bg-white space-y-1">
                                        {/* Week Stats */}
                                        <div className="grid grid-cols-3 gap-1 mb-2 p-1 bg-gray-50 rounded">
                                          <div className="text-center">
                                            <p className="text-sm font-bold text-blue-600">{weekSubmissions.length}</p>
                                            <p className="text-[8px] text-gray-500">Total</p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-sm font-bold text-green-600">{weekVetted}</p>
                                            <p className="text-[8px] text-gray-500">Vetted</p>
                                          </div>
                                          <div className="text-center">
                                            <p className="text-sm font-bold text-amber-600">{weekPending}</p>
                                            <p className="text-[8px] text-gray-500">Pending</p>
                                          </div>
                                        </div>
                                        
                                        {/* Submissions for this week */}
                                        {weekSubmissions.map((sub) => (
                                          <div key={sub.id} className="flex items-center justify-between p-2 bg-gray-50 rounded text-xs">
                                            <div className="min-w-0 flex-1">
                                              <p className="font-medium truncate">{sub.subject}</p>
                                              <p className="text-[10px] text-gray-500">
                                                {sub.class_level} • {sub.submitted_at ? new Date(sub.submitted_at).toLocaleDateString() : 'N/A'}
                                              </p>
                                            </div>
                                            <div className="flex items-center gap-1 shrink-0">
                                              {sub.vetted ? (
                                                <Badge className="bg-green-100 text-green-700 text-[9px]">
                                                  <CheckCircle className="h-2.5 w-2.5 mr-0.5" />Vetted
                                                </Badge>
                                              ) : (
                                                <Badge variant="outline" className="text-amber-600 border-amber-400 text-[9px]">
                                                  <Clock className="h-2.5 w-2.5 mr-0.5" />Pending
                                                </Badge>
                                              )}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              });
                            })()}
                          </div>
                        )}
                      </div>

                      {/* Support Action Button */}
                      <div className="pt-2 border-t">
                        <Button
                          onClick={() => {
                            // Find the school this teacher belongs to
                            const school = supervisionData?.schools?.find(s => 
                              s.teachers?.some(t => t.id === selectedTeacher)
                            );
                            openSupportActionModal(
                              teacherDetails.teacher,
                              school?.headteacher,
                              school
                            );
                          }}
                          className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                          data-testid="support-action-btn"
                        >
                          <HeartHandshake className="h-4 w-4 mr-2" />
                          Give Support Action
                        </Button>
                        <p className="text-[10px] text-gray-500 text-center mt-1">
                          Provide improvement support for underperforming areas
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* Reports Tab */}
          <TabsContent value="reports" className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
            {/* District Capital Town Setting */}
            <Card className="border-amber-300 bg-amber-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row sm:items-end gap-3">
                  <div className="flex-1">
                    <label className="block text-xs sm:text-sm font-medium mb-1 text-amber-800">
                      <MapPin className="h-3 w-3 inline mr-1" />
                      District Capital Town
                    </label>
                    <p className="text-[10px] text-amber-600 mb-2">
                      Enter the town name (usually the district capital where the Education Directorate is located). This will appear in the &quot;Submitted To&quot; section of your reports.
                    </p>
                    <Input
                      type="text"
                      value={districtCapitalTown}
                      onChange={(e) => setDistrictCapitalTown(e.target.value)}
                      placeholder="e.g., Akim Oda, Koforidua, Kumasi..."
                      className="text-sm"
                    />
                  </div>
                  <Button
                    onClick={saveDistrictCapitalTown}
                    disabled={savingTown}
                    className="bg-amber-600 hover:bg-amber-700 text-white text-xs sm:text-sm"
                  >
                    {savingTown ? (
                      <><Loader2 className="h-3 w-3 animate-spin mr-1" />Saving...</>
                    ) : (
                      <><Save className="h-3 w-3 mr-1" />Save Town</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Report Type Selector */}
            <Card>
              <CardContent className="p-3 sm:p-4">
                <label className="block text-xs sm:text-sm font-medium mb-2">Select Report Type</label>
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={selectedReportType === 'start_of_term' ? 'default' : 'outline'}
                    onClick={() => setSelectedReportType('start_of_term')}
                    className={`flex-1 text-xs sm:text-sm ${selectedReportType === 'start_of_term' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                  >
                    Start of Term
                  </Button>
                  <Button
                    type="button"
                    variant={selectedReportType === 'end_of_term' ? 'default' : 'outline'}
                    onClick={() => setSelectedReportType('end_of_term')}
                    className={`flex-1 text-xs sm:text-sm ${selectedReportType === 'end_of_term' ? 'bg-green-600 hover:bg-green-700' : ''}`}
                  >
                    End of Term
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* === START OF TERM REPORT CONTENT === */}
            {selectedReportType === 'start_of_term' && (
              <>
                {/* Load Report Data Card - TOP */}
                <Card className="border-blue-300 bg-blue-50/50">
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                        School Attendance Data
                      </CardTitle>
                      <Button
                        size="sm"
                        onClick={() => loadAllSchoolsReportData('start_of_term')}
                        disabled={loadingReportData === 'all'}
                        className="bg-blue-600 hover:bg-blue-700 text-xs"
                      >
                        {loadingReportData === 'all' ? (
                          <><Loader2 className="h-3 w-3 animate-spin mr-1" />Loading...</>
                        ) : (
                          <><Download className="h-3 w-3 mr-1" />Load All Data</>
                        )}
                      </Button>
                    </div>
                    <CardDescription className="text-xs">
                      Teacher attendance and learner enrollment by class from headteacher submissions
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    {dashboardData?.folders?.length === 0 ? (
                      <p className="text-gray-500 text-xs">No schools added yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {dashboardData?.folders?.map((folder) => (
                          <div key={folder.id} className="border rounded-lg p-3 bg-white">
                            <div className="flex items-center justify-between mb-3 pb-2 border-b">
                              <div>
                                <p className="font-semibold text-sm">{folder.school_name}</p>
                                <p className="text-xs text-gray-500">{folder.headteacher_name || 'No headteacher'}</p>
                              </div>
                              {!schoolReportData[folder.id] && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => loadSchoolReportData(folder.id, 'start_of_term')}
                                  disabled={loadingReportData === folder.id}
                                  className="text-xs"
                                >
                                  {loadingReportData === folder.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : 'Load'}
                                </Button>
                              )}
                            </div>

                            {schoolReportData[folder.id] ? (
                              schoolReportData[folder.id].has_report ? (
                                <div className="space-y-3">
                                  {/* Teacher Attendance */}
                                  <div>
                                    <h5 className="text-xs font-semibold text-blue-700 mb-2 flex items-center gap-1">
                                      <Users className="h-3 w-3" /> Teacher Attendance
                                    </h5>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                                      {schoolReportData[folder.id].teacher_attendance?.map((t, idx) => (
                                        <div key={idx} className={`px-2 py-1 rounded text-xs ${t.is_present ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                          <span className="font-medium">{t.teacher_name}</span>
                                          <span className="text-[10px] ml-1">({t.is_present ? '✓' : '✗'})</span>
                                        </div>
                                      ))}
                                    </div>
                                    <div className="mt-2 text-xs text-gray-600">
                                      Present: {schoolReportData[folder.id].teacher_attendance?.filter(t => t.is_present).length || 0} / 
                                      {schoolReportData[folder.id].teacher_attendance?.length || 0}
                                    </div>
                                  </div>

                                  {/* Class Attendance */}
                                  <div>
                                    <h5 className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                                      <School className="h-3 w-3" /> Learner Attendance by Class
                                    </h5>
                                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                                      {Object.entries(schoolReportData[folder.id].class_attendance || {}).map(([cls, count]) => (
                                        count && parseInt(count) > 0 ? (
                                          <div key={cls} className="bg-purple-50 px-2 py-1 rounded text-xs">
                                            <span className="font-medium">{cls}:</span> {count}
                                          </div>
                                        ) : null
                                      ))}
                                    </div>
                                  </div>

                                  {/* Remarks */}
                                  {schoolReportData[folder.id].remarks && (
                                    <div className="bg-gray-50 p-2 rounded text-xs">
                                      <span className="font-medium">Remarks:</span> {schoolReportData[folder.id].remarks}
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500 text-center py-2">{schoolReportData[folder.id].message}</p>
                              )
                            ) : (
                              <p className="text-xs text-gray-400 text-center py-2">Click &quot;Load&quot; to fetch attendance data</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* AI Report Generation (with Preview & Edit) */}
                <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                      <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                      Generate Official Circuit Report
                    </CardTitle>
                    <CardDescription className="text-xs">
                      Generates a report using actual data. Preview, edit, then finalize to PDF.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0 space-y-2">
                    <div className="text-[10px] text-gray-600 bg-white/60 p-2 rounded">
                      <p className="font-medium">Workflow: Generate → Preview → Edit → Finalize → Download PDF</p>
                    </div>
                    <Button 
                      onClick={() => generateAIReport('start_of_term')}
                      disabled={generatingAIReport}
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      {generatingAIReport ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><Eye className="h-4 w-4 mr-2" />Generate &amp; Preview Report</>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Circuit PDF Report */}
                <Card className="border-blue-200 bg-blue-50/30">
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                      Download Circuit Data Summary (PDF)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <Button 
                      onClick={() => generateCircuitReport('start_of_term')}
                      disabled={generatingCircuitReport}
                      className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
                    >
                      {generatingCircuitReport ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><Download className="h-4 w-4 mr-2" />Download Start of Term PDF</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}

            {/* === END OF TERM REPORT CONTENT === */}
            {selectedReportType === 'end_of_term' && (
              <>
                {/* Teacher Info & Verified Work Output - TOP */}
                <Card className="border-green-300 bg-green-50/50">
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                        <Users className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                        Teacher Data & Verified Work Output
                      </CardTitle>
                      <Button
                        size="sm"
                        onClick={() => loadAllSchoolsReportData('end_of_term')}
                        disabled={loadingReportData === 'all'}
                        className="bg-green-600 hover:bg-green-700 text-xs"
                      >
                        {loadingReportData === 'all' ? (
                          <><Loader2 className="h-3 w-3 animate-spin mr-1" />Loading...</>
                        ) : (
                          <><Download className="h-3 w-3 mr-1" />Load All Data</>
                        )}
                      </Button>
                    </div>
                    <CardDescription className="text-xs">
                      Teacher rank, portfolio, attendance, lesson notes status, and verified work output
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    {dashboardData?.folders?.length === 0 ? (
                      <p className="text-gray-500 text-xs">No schools added yet.</p>
                    ) : (
                      <div className="space-y-4">
                        {dashboardData?.folders?.map((folder) => (
                          <div key={folder.id} className="border rounded-lg p-3 bg-white">
                            <div className="flex items-center justify-between mb-3 pb-2 border-b">
                              <div>
                                <p className="font-semibold text-sm">{folder.school_name}</p>
                                <p className="text-xs text-gray-500">{folder.headteacher_name || 'No headteacher'}</p>
                              </div>
                              {!schoolReportData[folder.id] && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => loadSchoolReportData(folder.id, 'end_of_term')}
                                  disabled={loadingReportData === folder.id}
                                  className="text-xs"
                                >
                                  {loadingReportData === folder.id ? (
                                    <Loader2 className="h-3 w-3 animate-spin" />
                                  ) : 'Load'}
                                </Button>
                              )}
                            </div>

                            {schoolReportData[folder.id] ? (
                              schoolReportData[folder.id].has_report ? (
                                <div className="space-y-4">
                                  {/* Teachers Info Table */}
                                  <div>
                                    <h5 className="text-xs font-semibold text-green-700 mb-2 flex items-center gap-1">
                                      <Users className="h-3 w-3" /> Teacher Details
                                    </h5>
                                    <div className="overflow-x-auto">
                                      <table className="w-full min-w-[500px] text-[10px] sm:text-xs">
                                        <thead className="bg-green-50">
                                          <tr>
                                            <th className="px-2 py-1 text-left font-semibold">Name</th>
                                            <th className="px-2 py-1 text-left font-semibold">Rank</th>
                                            <th className="px-2 py-1 text-left font-semibold">Portfolio</th>
                                            <th className="px-2 py-1 text-center font-semibold">Total Att.</th>
                                            <th className="px-2 py-1 text-center font-semibold">Lesson Notes</th>
                                          </tr>
                                        </thead>
                                        <tbody className="divide-y">
                                          {schoolReportData[folder.id].teachers_info?.map((t, idx) => (
                                            <tr key={idx} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                              <td className="px-2 py-1 font-medium">{t.teacher_name}</td>
                                              <td className="px-2 py-1">{t.teacher_rank || '-'}</td>
                                              <td className="px-2 py-1">{t.teacher_portfolio || '-'}</td>
                                              <td className="px-2 py-1 text-center">{t.total_attendance || 0}</td>
                                              <td className="px-2 py-1 text-center">
                                                <Badge className={`text-[9px] ${
                                                  t.lesson_notes_received === 'All weeks' ? 'bg-green-600' :
                                                  t.lesson_notes_received === 'Some weeks' ? 'bg-yellow-500' :
                                                  'bg-red-500'
                                                }`}>
                                                  {t.lesson_notes_received || 'None'}
                                                </Badge>
                                              </td>
                                            </tr>
                                          ))}
                                        </tbody>
                                      </table>
                                    </div>
                                  </div>

                                  {/* Verified Work Output */}
                                  {schoolReportData[folder.id].verified_work_output?.length > 0 && (
                                    <div>
                                      <h5 className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                                        <FileText className="h-3 w-3" /> Verified Work Output
                                      </h5>
                                      <div className="space-y-2">
                                        {schoolReportData[folder.id].verified_work_output.map((wo, idx) => (
                                          <div key={idx} className="bg-purple-50 p-2 rounded">
                                            <p className="text-xs font-medium text-purple-700 mb-1">{wo.teacher_name}</p>
                                            <div className="overflow-x-auto">
                                              <table className="w-full min-w-[400px] text-[10px]">
                                                <thead className="bg-purple-100">
                                                  <tr>
                                                    <th className="px-1 py-1 text-left">Subject</th>
                                                    {workOutputWeeks.map(w => (
                                                      <th key={w} className="px-1 py-1 text-center">W{w}</th>
                                                    ))}
                                                    <th className="px-1 py-1 text-center bg-green-100">Total</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {Object.keys(wo.work_output || {}).map((subject) => {
                                                    const weeks = wo.work_output[subject] || {};
                                                    const total = Object.values(weeks).reduce((sum, v) => sum + (parseInt(v) || 0), 0);
                                                    return (
                                                      <tr key={subject} className="border-t">
                                                        <td className="px-1 py-1 font-medium">{subject}</td>
                                                        {workOutputWeeks.map(w => (
                                                          <td key={w} className="px-1 py-1 text-center">
                                                            {weeks[w] ? <span className="text-green-600">{weeks[w]}</span> : '-'}
                                                          </td>
                                                        ))}
                                                        <td className="px-1 py-1 text-center bg-green-50 font-bold text-green-700">{total}</td>
                                                      </tr>
                                                    );
                                                  })}
                                                </tbody>
                                              </table>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <p className="text-xs text-gray-500 text-center py-2">{schoolReportData[folder.id].message}</p>
                              )
                            ) : (
                              <p className="text-xs text-gray-400 text-center py-2">Click &quot;Load&quot; to fetch teacher data</p>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* AI Report Generation (with Preview & Edit) */}
                <Card className="border-purple-200 bg-gradient-to-r from-purple-50 to-indigo-50">
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                      <FileText className="h-4 w-4 sm:h-5 sm:w-5 text-purple-600" />
                      Generate Official Circuit Report
                    </CardTitle>
                    <CardDescription className="text-xs">
                      AI analyzes work output, attendance, and lesson notes. Preview, edit, then finalize.
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0 space-y-2">
                    <div className="text-[10px] text-gray-600 bg-white/60 p-2 rounded">
                      <p className="font-medium">Workflow: Generate → Preview → Edit → Finalize → Download PDF</p>
                    </div>
                    <Button 
                      onClick={() => generateAIReport('end_of_term')}
                      disabled={generatingAIReport}
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700"
                    >
                      {generatingAIReport ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><Eye className="h-4 w-4 mr-2" />Generate &amp; Preview Report</>
                      )}
                    </Button>
                  </CardContent>
                </Card>

                {/* Circuit PDF */}
                <Card className="border-green-200 bg-green-50/30">
                  <CardHeader className="p-3 sm:p-4 pb-2">
                    <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                      <Building2 className="h-4 w-4 sm:h-5 sm:w-5 text-green-600" />
                      Download Circuit Data Summary (PDF)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-3 sm:p-4 pt-0">
                    <Button 
                      onClick={() => generateCircuitReport('end_of_term')}
                      disabled={generatingCircuitReport}
                      className="w-full sm:w-auto bg-green-600 hover:bg-green-700"
                    >
                      {generatingCircuitReport ? (
                        <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                      ) : (
                        <><Download className="h-4 w-4 mr-2" />Download End of Term PDF</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </>
            )}

            {/* Individual School Reports (Both report types) */}
            <Card>
              <CardHeader className="p-3 sm:p-4 pb-2">
                <CardTitle className="text-sm sm:text-base flex items-center gap-2">
                  <School className="h-4 w-4 sm:h-5 sm:w-5" />
                  Individual School Reports (PDF)
                </CardTitle>
                <CardDescription className="text-xs">
                  Download {selectedReportType === 'start_of_term' ? 'Start' : 'End'} of Term data for each school
                </CardDescription>
              </CardHeader>
              <CardContent className="p-3 sm:p-4 pt-0">
                {dashboardData?.folders?.length === 0 ? (
                  <p className="text-gray-500 text-xs">No schools added yet.</p>
                ) : (
                  <div className="space-y-2">
                    {dashboardData?.folders?.map((folder) => (
                      <div key={folder.id} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                        <div className="min-w-0 flex-1 mr-2">
                          <p className="font-medium text-xs sm:text-sm truncate">{folder.school_name}</p>
                          <p className="text-[10px] text-gray-500">{folder.headteacher_name || 'No headteacher'}</p>
                        </div>
                        <Button 
                          size="sm"
                          variant={folder.has_reports ? "default" : "outline"}
                          onClick={() => generateSchoolReport(folder.id, folder.school_name, selectedReportType)}
                          disabled={generatingReport === folder.id || !folder.has_reports}
                          className={`shrink-0 h-8 ${folder.has_reports ? (selectedReportType === 'start_of_term' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700') : ''}`}
                        >
                          {generatingReport === folder.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <><Download className="h-4 w-4 mr-1" /><span className="hidden sm:inline">PDF</span></>
                          )}
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-purple-600" />
                  Applications
                </CardTitle>
                <CardDescription>
                  Review and manage teacher and headteacher applications escalated to you
                </CardDescription>
              </CardHeader>
              <CardContent>
                {loadingApplications ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                  </div>
                ) : applications.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <FileText className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                    <p>No applications received yet</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {applications.map((app) => (
                      <div
                        key={app.id}
                        className={`border rounded-lg p-4 ${app.siso_read_status !== 'read' ? 'bg-blue-50 border-blue-200' : 'bg-white'}`}
                      >
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              {app.siso_read_status !== 'read' && (
                                <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                              )}
                              <span className="font-medium text-gray-900">
                                {app.source === 'teacher' ? app.teacher_name : app.headteacher_name}
                              </span>
                              <span className="text-xs px-2 py-0.5 bg-gray-100 rounded-full">
                                {app.source === 'teacher' ? 'Teacher' : 'Headteacher'}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600">{app.application_type?.replace(/_/g, ' ')}</p>
                            <p className="text-xs text-gray-400">
                              {app.school_name} • {new Date(app.submitted_at).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            {getStatusBadge(app.status)}
                            {!['approved_by_siso', 'disapproved_by_siso', 'escalated_to_director', 'escalated_to_secretary', 'awaiting_escalation_payment'].includes(app.status) && (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => viewApplication(app)}
                                  data-testid={`read-app-${app.id}`}
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Read
                                </Button>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleApprove(app)}
                                  disabled={processingAction}
                                >
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-red-600 border-red-300 hover:bg-red-50"
                                  onClick={() => {
                                    setSelectedApplication(app);
                                    setShowDisapproveModal(true);
                                  }}
                                >
                                  <XCircle className="h-3 w-3 mr-1" />
                                  Disapprove
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="text-indigo-600 border-indigo-300 hover:bg-indigo-50"
                                  onClick={() => startEscalation(app)}
                                >
                                  <FileText className="h-3 w-3 mr-1" />
                                  {app.siso_memo || app.siso_memo_saved ? 'Edit Memo' : 'Escalate'}
                                </Button>
                                {(app.siso_memo || app.siso_memo_saved) && (
                                  <Button
                                    size="sm"
                                    className="bg-indigo-600 hover:bg-indigo-700 text-white"
                                    onClick={async (e) => {
                                      e.stopPropagation();
                                      try {
                                        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                        const res = await authenticatedFetch(`${backendUrl}/api/siso/application/${app.id}/action`, {
                                          method: 'POST',
                                          headers: { 'Content-Type': 'application/json' },
                                          body: JSON.stringify({
                                            action: 'submit_to_director',
                                            source: app.source || 'teacher',
                                            comment: 'Submitted to Director',
                                            formatted_memo: app.siso_memo
                                          })
                                        });
                                        if (res.ok) {
                                          toast.success('Application submitted to Director');
                                          loadApplications();
                                        } else {
                                          const err = await res.json();
                                          toast.error(err.detail || 'Failed to submit');
                                        }
                                      } catch (err) { toast.error('Error submitting'); }
                                    }}
                                    data-testid={`submit-to-director-${app.id}`}
                                  >
                                    <Forward className="h-3 w-3 mr-1" />
                                    Submit to Director
                                  </Button>
                                )}
                              </>
                            )}
                            {['approved_by_siso', 'disapproved_by_siso', 'escalated_to_director', 'escalated_to_secretary', 'awaiting_escalation_payment'].includes(app.status) && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => viewApplication(app)}
                              >
                                <Eye className="h-3 w-3 mr-1" />
                                View
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Application Detail Modal - Enhanced with Full Page Views */}
            {showApplicationDetail && selectedApplication && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
                <Card className="w-full max-w-4xl my-2 sm:my-4">
                  <CardHeader className="p-3 sm:p-6">
                    <CardTitle className="flex items-start sm:items-center justify-between gap-2">
                      <span className="flex flex-wrap items-center gap-1 sm:gap-2 text-sm sm:text-base">
                        <FileText className="h-4 w-4 sm:h-5 sm:w-5 shrink-0" />
                        <span className="break-words">Application from {selectedApplication.source === 'teacher' 
                          ? selectedApplication.teacher_name 
                          : selectedApplication.headteacher_name}</span>
                        {getStatusBadge(selectedApplication.status)}
                      </span>
                      <Button variant="ghost" size="sm" className="shrink-0" onClick={() => {
                        setShowApplicationDetail(false);
                        setSelectedApplication(null);
                      }}>
                        <X className="h-4 w-4" />
                      </Button>
                    </CardTitle>
                    <p className="text-xs sm:text-sm text-gray-500">
                      {selectedApplication.application_type?.replace(/_/g, ' ')} • Submitted {new Date(selectedApplication.submitted_at).toLocaleString()}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6">
                    {/* Tabs for switching between documents */}
                    <div className="flex flex-col sm:flex-row gap-2 border-b pb-2">
                      <Button
                        variant={!showHeadteacherMemoTab ? "default" : "outline"}
                        size="sm"
                        onClick={() => setShowHeadteacherMemoTab(false)}
                        className={`w-full sm:w-auto text-xs sm:text-sm ${!showHeadteacherMemoTab ? "bg-blue-600" : ""}`}
                      >
                        <FileText className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                        {selectedApplication.source === 'teacher' ? 'Teacher' : 'HT'} Application
                      </Button>
                      {selectedApplication.escalation_memo && selectedApplication.source === 'teacher' && (
                        <Button
                          variant={showHeadteacherMemoTab ? "default" : "outline"}
                          size="sm"
                          onClick={() => setShowHeadteacherMemoTab(true)}
                          className={`w-full sm:w-auto text-xs sm:text-sm ${showHeadteacherMemoTab ? "bg-amber-600" : "text-amber-700 border-amber-300"}`}
                        >
                          <Forward className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                          HT Memo
                        </Button>
                      )}
                    </div>
                    
                    {/* Document Preview Area */}
                    <div className="max-h-[40vh] sm:max-h-[50vh] overflow-y-auto bg-gray-50 rounded-lg border">
                      {!showHeadteacherMemoTab ? (
                        /* Original Application */
                        <div className="bg-white p-4 sm:p-8 shadow-sm mx-auto max-w-[21cm]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                          <div className="prose prose-sm max-w-none">
                            {selectedApplication.html_content ? (
                              <div dangerouslySetInnerHTML={{ __html: selectedApplication.html_content }} />
                            ) : selectedApplication.formatted_letter ? (
                              renderFormattedLetter(
                                selectedApplication.formatted_letter,
                                selectedApplication.signature_url ? `${process.env.REACT_APP_BACKEND_URL}${selectedApplication.signature_url}` : null,
                                null
                              )
                            ) : (
                              <pre className="whitespace-pre-wrap font-serif text-sm">{selectedApplication.content}</pre>
                            )}
                          </div>
                        </div>
                      ) : (
                        /* Headteacher's Escalation Memo */
                        <div className="bg-white p-4 sm:p-8 shadow-sm mx-auto max-w-[21cm]" style={{ fontFamily: 'Georgia, "Times New Roman", serif' }}>
                          <div className="text-center mb-6">
                            <h2 className="text-lg font-bold">FORWARDING MEMORANDUM</h2>
                            <p className="text-sm text-gray-600">From: Headteacher to SISO</p>
                          </div>
                          <div className="prose prose-sm max-w-none">
                            {(() => {
                              const memo = selectedApplication.headteacher_memo_html || selectedApplication.headteacher_memo || selectedApplication.escalation_memo || '';
                              const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                              // Embed HT signature/stamp into the memo for display
                              let displayMemo = memo;
                              const htSigUrl = selectedApplication.headteacher_signature_url;
                              const htStampUrl = selectedApplication.headteacher_stamp_url;
                              if (htSigUrl) {
                                const fullSig = `${backendUrl}${htSigUrl}`;
                                const sigImg = `<div style="margin:10px 0;"><img src="${fullSig}" alt="Signature" style="max-height:70px;max-width:200px;" crossorigin="anonymous" /></div>`;
                                displayMemo = displayMemo.replace(/\[SIGNATURE_PLACEHOLDER\]/gi, sigImg).replace(/\[SIGNATURE\]/gi, '');
                              } else { displayMemo = displayMemo.replace(/\[SIGNATURE_PLACEHOLDER\]/gi, '').replace(/\[SIGNATURE\]/gi, ''); }
                              if (htStampUrl) {
                                const fullStamp = `${backendUrl}${htStampUrl}`;
                                const stampImg = `<div style="margin:5px 0;"><img src="${fullStamp}" alt="Stamp" style="max-height:90px;max-width:90px;" crossorigin="anonymous" /></div>`;
                                displayMemo = displayMemo.replace(/\[STAMP_PLACEHOLDER\]/gi, stampImg).replace(/\[STAMP\]/gi, '');
                              } else { displayMemo = displayMemo.replace(/\[STAMP_PLACEHOLDER\]/gi, '').replace(/\[STAMP\]/gi, ''); }

                              if (displayMemo.trim().startsWith('<')) {
                                return <div dangerouslySetInnerHTML={{ __html: displayMemo }} />;
                              }
                              return renderFormattedLetter(memo, htSigUrl ? `${backendUrl}${htSigUrl}` : null, htStampUrl ? `${backendUrl}${htStampUrl}` : null);
                            })()}
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Attachments Section - Display applicant's attachments */}
                    {selectedApplication.attachments && selectedApplication.attachments.length > 0 && (
                      <div className="border-t pt-3 sm:pt-4">
                        <h4 className="font-medium text-gray-700 flex items-center gap-2 mb-2 sm:mb-3 text-sm sm:text-base">
                          <Paperclip className="h-4 w-4" />
                          Attached Documents
                          <Badge className="bg-blue-100 text-blue-700">{selectedApplication.attachments.length}</Badge>
                        </h4>
                        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
                          {selectedApplication.attachments.map((att) => (
                            <button
                              key={att.id}
                              onClick={async () => {
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                try {
                                  const response = await authenticatedFetch(`${backendUrl}/api/application/attachment/${att.id}`);
                                  if (response.ok) {
                                    const blob = await response.blob();
                                    const url = URL.createObjectURL(blob);
                                    window.open(url, '_blank');
                                    setTimeout(() => URL.revokeObjectURL(url), 60000);
                                  } else { toast.error('Failed to open attachment'); }
                                } catch (e) { toast.error('Error opening attachment'); }
                              }}
                              className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg border hover:bg-blue-50 hover:border-blue-300 transition-colors cursor-pointer text-left"
                            >
                              <FileIcon className="h-4 w-4 text-blue-500 shrink-0" />
                              <span className="text-sm font-medium truncate">{att.filename}</span>
                              <Download className="h-3 w-3 text-gray-400 shrink-0" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Action Buttons - Only for non-processed applications */}
                    {!['approved_by_siso', 'disapproved_by_siso', 'escalated_to_director'].includes(selectedApplication.status) && (
                      <div className="border-t pt-3 sm:pt-4 flex flex-col sm:flex-row gap-2 sm:flex-wrap sm:justify-center">
                        <Button
                          className="bg-green-600 hover:bg-green-700 w-full sm:w-auto"
                          onClick={() => handleApprove(selectedApplication)}
                          disabled={processingAction}
                        >
                          {processingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                          Approve
                        </Button>
                        <Button
                          variant="outline"
                          className="text-red-600 border-red-300 hover:bg-red-50 w-full sm:w-auto"
                          onClick={() => setShowDisapproveModal(true)}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Disapprove
                        </Button>
                        <Button
                          variant="outline"
                          className="text-indigo-600 border-indigo-300 hover:bg-indigo-50 w-full sm:w-auto"
                          onClick={() => {
                            startEscalation(selectedApplication);
                            setShowApplicationDetail(false);
                          }}
                        >
                          <Forward className="h-4 w-4 mr-2" />
                          Escalate to Director
                        </Button>
                      </div>
                    )}
                    
                    {/* Action details for processed applications */}
                    {selectedApplication.siso_comment && (
                      <div className={`border-t pt-3 sm:pt-4 p-3 sm:p-4 rounded-lg ${
                        selectedApplication.status === 'approved_by_siso' ? 'bg-green-50 border border-green-200' :
                        selectedApplication.status === 'disapproved_by_siso' ? 'bg-red-50 border border-red-200' :
                        'bg-indigo-50 border border-indigo-200'
                      }`}>
                        <p className="font-medium mb-1">
                          {selectedApplication.status === 'approved_by_siso' && '✓ Approved by SISO'}
                          {selectedApplication.status === 'disapproved_by_siso' && '✗ Disapproved by SISO'}
                          {selectedApplication.status === 'escalated_to_director' && '↗ Escalated to Director'}
                        </p>
                        <p className="text-sm text-gray-600"><span className="font-medium">Comment:</span> {selectedApplication.siso_comment}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Disapprove Modal */}
            {showDisapproveModal && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
                <Card className="w-full max-w-md">
                  <CardHeader>
                    <CardTitle className="text-red-600">Disapprove Application</CardTitle>
                    <CardDescription>Please provide a reason for disapproval</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <textarea
                      value={disapproveComment}
                      onChange={(e) => setDisapproveComment(e.target.value)}
                      placeholder="Enter reason for disapproval..."
                      className="w-full h-32 p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                      data-testid="disapprove-comment-input"
                    />
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => {
                        setShowDisapproveModal(false);
                        setDisapproveComment('');
                      }}>
                        Cancel
                      </Button>
                      <Button
                        className="bg-red-600 hover:bg-red-700"
                        onClick={handleDisapprove}
                        disabled={processingAction || !disapproveComment.trim()}
                      >
                        {processingAction ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <XCircle className="h-4 w-4 mr-2" />}
                        Disapprove
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* Escalation to Director Modal */}
            {escalatingApp && (
              <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto">
                <Card className="w-full max-w-3xl my-2 sm:my-4">
                  <CardHeader className="p-4 sm:p-6">
                    <CardTitle className="flex items-center gap-2 text-indigo-600">
                      <FileText className="h-5 w-5" />
                      Escalate to District Director
                    </CardTitle>
                    <CardDescription>
                      Create an official memo for escalating {escalatingApp.source === 'teacher' ? escalatingApp.teacher_name : escalatingApp.headteacher_name}'s application
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4 p-4 sm:p-6">
                    {!showMemoPreview ? (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Key Facts & Recommendations for Memo
                          </label>
                          <textarea
                            value={escalateMemoText}
                            onChange={(e) => setEscalateMemoText(e.target.value)}
                            placeholder="Enter the key facts, your observations, and recommendations for the Director...

Example:
- Brief summary of the application
- Your assessment of the request
- Supporting information
- Your recommendation"
                            className="w-full h-40 p-3 border-2 border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            data-testid="escalate-memo-input"
                          />
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button variant="outline" onClick={() => {
                            setEscalatingApp(null);
                            setEscalateMemoText('');
                          }}>
                            Cancel
                          </Button>
                          <Button
                            className="bg-indigo-600 hover:bg-indigo-700"
                            onClick={generateDirectorMemo}
                            disabled={generatingMemo || !escalateMemoText.trim()}
                          >
                            {generatingMemo ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</>
                            ) : (
                              <><Sparkles className="h-4 w-4 mr-2" />Generate Memo</>
                            )}
                          </Button>
                        </div>
                      </>
                    ) : !memoSaved ? (
                      /* Tiptap Editor for memo editing */
                      <div className="space-y-3">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                          <p className="text-sm text-blue-700 flex items-center gap-2">
                            <FileEdit className="h-4 w-4" />
                            Review and edit the memo below before saving. Your edits will be preserved in the final PDF.
                          </p>
                        </div>
                        <div className="bg-white shadow-lg rounded-lg overflow-hidden border-2 border-indigo-200">
                          <RichTextEditor
                            content={editedMemoHtml}
                            onChange={setEditedMemoHtml}
                            placeholder="Edit your memo here..."
                            minHeight="250px"
                            className="[&_.ProseMirror]:px-4 [&_.ProseMirror]:sm:px-10 [&_.ProseMirror]:py-6 [&_.ProseMirror]:sm:py-8 [&_.ProseMirror]:font-serif [&_.ProseMirror]:text-sm [&_.ProseMirror]:sm:text-base [&_.ProseMirror]:leading-6 [&_.ProseMirror]:sm:leading-7"
                          />
                        </div>
                        <div className="flex flex-wrap gap-2 pt-2">
                          <Button variant="outline" size="sm" onClick={() => {
                            setShowMemoPreview(false);
                            setFormattedDirectorMemo('');
                            setEditedMemoHtml('');
                          }}>
                            ← Back
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={generateDirectorMemo}
                            disabled={generatingMemo}
                          >
                            {generatingMemo ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-1" />Regenerate</>}
                          </Button>
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            onClick={async () => {
                              setSavingMemo(true);
                              try {
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                const res = await authenticatedFetch(`${backendUrl}/api/siso/save-memo-draft`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({
                                    application_id: escalatingApp.id,
                                    html_content: editedMemoHtml,
                                    source: escalatingApp.source
                                  })
                                });
                                if (res.ok) {
                                  setMemoSaved(true);
                                  toast.success('SISO memo saved and PDF generated!');
                                } else {
                                  const err = await res.json();
                                  throw new Error(err.detail || 'Failed to save memo');
                                }
                              } catch (e) { toast.error(e.message || 'Failed to save memo'); }
                              finally { setSavingMemo(false); }
                            }}
                            disabled={savingMemo}
                            data-testid="save-siso-memo-btn"
                          >
                            {savingMemo ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><CheckCircle className="h-4 w-4 mr-2" />Save Memo</>}
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* After Save: Read-only preview + Escalate to Director */
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <Badge className="bg-green-100 text-green-700">Memo Saved</Badge>
                        </div>
                        <div className="bg-white border-2 border-indigo-200 rounded-lg p-4 sm:p-6 shadow-inner max-h-[300px] overflow-y-auto mx-auto" style={{ maxWidth: '210mm' }}>
                          <div className="font-['Georgia',_'Times_New_Roman',_serif] text-base leading-7 text-gray-900" dangerouslySetInnerHTML={{ __html: editedMemoHtml }} />
                        </div>
                        
                        {/* Application Support Fee Display */}
                        {applicationSupportFee > 0 && (
                          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center gap-3">
                            <Banknote className="h-5 w-5 text-amber-600" />
                            <div>
                              <p className="text-sm font-medium text-amber-800">Application Support Fee</p>
                              <p className="text-lg font-bold text-amber-900">GHS {applicationSupportFee.toFixed(2)}</p>
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 justify-end flex-wrap">
                          <Button variant="outline" size="sm" onClick={() => setMemoSaved(false)}>
                            <FileEdit className="h-4 w-4 mr-1" />Edit Again
                          </Button>
                          <Button
                            className="bg-indigo-600 hover:bg-indigo-700 text-white"
                            onClick={async () => {
                              if (applicationSupportFee > 0) {
                                // Save SISO memo to the application first, then initiate payment
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                try {
                                  await authenticatedFetch(`${backendUrl}/api/siso/application/${escalatingApp.id}/action`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      action: 'save_siso_memo',
                                      formatted_memo: editedMemoHtml
                                    })
                                  });
                                } catch (e) { /* memo already saved via draft endpoint */ }
                                await handlePayToSubmit();
                              } else {
                                // No payment required — submit directly to director
                                const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
                                try {
                                  const res = await authenticatedFetch(`${backendUrl}/api/siso/application/${escalatingApp.id}/action`, {
                                    method: 'POST',
                                    headers: { 'Content-Type': 'application/json' },
                                    body: JSON.stringify({
                                      action: 'submit_to_director',
                                      formatted_memo: editedMemoHtml
                                    })
                                  });
                                  if (res.ok) {
                                    toast.success('Application escalated to Director successfully!');
                                  } else {
                                    const err = await res.json();
                                    throw new Error(err.detail || 'Failed to escalate');
                                  }
                                } catch (e) { toast.error(e.message || 'Failed to escalate'); }
                                setEscalatingApp(null);
                                setEscalateMemoText('');
                                setFormattedDirectorMemo('');
                                setEditedMemoHtml('');
                                setShowMemoPreview(false);
                                setMemoSaved(false);
                                loadApplications();
                              }
                            }}
                            disabled={processingAction || processingPayment}
                            data-testid="escalate-to-director-btn"
                          >
                            {(processingAction || processingPayment) ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Processing...</>
                            ) : applicationSupportFee > 0 ? (
                              <><Send className="h-4 w-4 mr-2" />Escalate (Pending Payment)</>
                            ) : (
                              <><Send className="h-4 w-4 mr-2" />Escalate to Director</>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  My Profile & Settings
                </CardTitle>
                <CardDescription>
                  Manage your profile picture, digital signature, stamp, and contact details
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {profileLoading ? (
                  <div className="flex justify-center py-8">
                    <Loader2 className="h-8 w-8 animate-spin text-green-600" />
                  </div>
                ) : (
                  <>
                    {/* Upload Sections - Inline on Desktop */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {/* Profile Picture */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <Users className="h-4 w-4 text-green-600" />
                          Profile Picture
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.profile_picture ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.profile_picture}`} alt="Profile" className="w-20 h-20 rounded-full object-cover border-2 border-green-200" />
                          ) : (
                            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                              <Users className="h-8 w-8 text-gray-400" />
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleProfilePictureUpload} disabled={uploadingProfilePic} />
                              <Button variant="outline" size="sm" disabled={uploadingProfilePic} className="w-full" asChild>
                                <span>{uploadingProfilePic ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.profile_picture && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('profile_picture')}>
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Digital Signature */}
                      <div className="border rounded-lg p-4">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <FileEdit className="h-4 w-4 text-blue-600" />
                          Digital Signature
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.digital_signature ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.digital_signature}`} alt="Signature" className="w-28 h-14 object-contain border rounded bg-white p-1" />
                          ) : (
                            <div className="w-28 h-14 bg-gray-100 flex items-center justify-center border rounded">
                              <span className="text-xs text-gray-400">No signature</span>
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleSignatureUpload} disabled={uploadingSignature} />
                              <Button variant="outline" size="sm" disabled={uploadingSignature} className="w-full" asChild>
                                <span>{uploadingSignature ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.digital_signature && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('signature')}>
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Official Stamp */}
                      <div className="border rounded-lg p-4 bg-emerald-50">
                        <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2 text-sm">
                          <Stamp className="h-4 w-4 text-emerald-600" />
                          Official Stamp
                        </h3>
                        <div className="flex flex-col items-center gap-3">
                          {userProfile?.digital_stamp ? (
                            <img src={`${process.env.REACT_APP_BACKEND_URL}${userProfile.digital_stamp}`} alt="Stamp" className="w-16 h-16 object-contain border rounded bg-white p-1" />
                          ) : (
                            <div className="w-16 h-16 bg-white flex items-center justify-center border rounded">
                              <span className="text-xs text-gray-400">No stamp</span>
                            </div>
                          )}
                          <div className="flex flex-col gap-2 w-full">
                            <label className="cursor-pointer w-full">
                              <input type="file" accept="image/*" className="hidden" onChange={handleStampUpload} disabled={uploadingStamp} />
                              <Button variant="outline" size="sm" disabled={uploadingStamp} className="w-full" asChild>
                                <span>{uploadingStamp ? <><Loader2 className="h-3 w-3 mr-1 animate-spin" />Uploading</> : <><Upload className="h-3 w-3 mr-1" />Upload</>}</span>
                              </Button>
                            </label>
                            {userProfile?.digital_stamp && (
                              <Button variant="outline" size="sm" className="text-red-600 border-red-300 hover:bg-red-50 w-full" onClick={() => handleDeleteUpload('stamp')}>
                                <Trash2 className="h-3 w-3 mr-1" />Remove
                              </Button>
                            )}
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 text-center">For memos to directorate</p>
                      </div>
                    </div>

                    {/* Contact Details Section */}
                    <div className="border rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <BookOpen className="h-5 w-5 text-purple-600" />
                        Contact Details
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Town/City</label>
                          <input type="text" value={profileFormData.town} onChange={(e) => setProfileFormData(prev => ({ ...prev, town: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Enter your town" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
                          <input type="tel" value={profileFormData.phone} onChange={(e) => setProfileFormData(prev => ({ ...prev, phone: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="e.g., 0244123456" />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Office File Number</label>
                          <input type="text" value={profileFormData.file_number} onChange={(e) => setProfileFormData(prev => ({ ...prev, file_number: e.target.value }))} className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500" placeholder="Enter your file number" />
                        </div>
                      </div>
                      <div className="mt-4 flex justify-end">
                        <Button onClick={handleSaveProfile} disabled={savingProfile} className="bg-green-600 hover:bg-green-700">
                          {savingProfile ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Saving...</> : <><CheckCircle className="h-4 w-4 mr-2" />Save Details</>}
                        </Button>
                      </div>
                    </div>

                    {/* Account Info */}
                    {userProfile && (
                      <div className="border rounded-lg p-4 bg-gray-50">
                        <h3 className="font-semibold text-gray-900 mb-3">Account Information</h3>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
                          <div><span className="text-gray-500">Name:</span></div>
                          <div className="font-medium">{userProfile.name}</div>
                          <div><span className="text-gray-500">Email:</span></div>
                          <div className="font-medium">{userProfile.email}</div>
                          <div><span className="text-gray-500">Circuit:</span></div>
                          <div className="font-medium">{userProfile.circuit_name}</div>
                          <div><span className="text-gray-500">District:</span></div>
                          <div className="font-medium">{userProfile.district_name}</div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Promotion Exams Preparation Tab */}
          <TabsContent value="promotion_exams">
            <PromotionPreparation />
          </TabsContent>
        </Tabs>
      </div>
      
      {/* Report Editor Modal */}
      <ReportEditor
        isOpen={isReportEditorOpen}
        onClose={() => setIsReportEditorOpen(false)}
        reportData={currentReportData}
        reportType={currentReportType}
        onReportUpdated={() => {
          // Optionally refresh data
        }}
      />
    </div>
  );
};

export default SISODashboard;
